// Thin helpers on top of the supabase client. All queries scope by firm_id
// automatically via RLS, but we still pass it explicitly on inserts where the
// DB needs it (RLS with-check requires it).
//
// Each helper returns { data, error }. Pages are responsible for rendering
// error states. Keep this file dependency-free so it's easy to mock.

import { supabase } from '../services/supabaseClient';
import { withTimeout } from './fetchWithTimeout';

// Default query timeout — 25 s gives a cold Supabase DB enough time to wake
// up without making users wait forever. Every list* helper wraps its query
// builder in this so pages never hang indefinitely regardless of whether they
// have their own withTimeout wrapper.
const QUERY_TIMEOUT = 25_000;

const from = (t) => supabase.from(t);

// ---------------- super-admin -----------------
export const getAllFirmStats = () =>
  supabase.rpc('get_all_firm_stats');

export const getFirmMatters  = (firmId) => supabase.rpc('get_firm_matters',  { p_firm_id: firmId });
export const getFirmContacts = (firmId) => supabase.rpc('get_firm_contacts', { p_firm_id: firmId });
export const getFirmInvoices = (firmId) => supabase.rpc('get_firm_invoices', { p_firm_id: firmId });

export const getFirmDetailStats = (firmId) =>
  supabase.rpc('get_firm_detail_stats', { p_firm_id: firmId });

// ── Firm lifecycle (super-admin only) ──
//
// IMPORTANT: these are now SECURITY DEFINER RPCs (migration 132) that
// enforce suspension server-side. The previous direct .update() calls
// only wrote cosmetic columns — they did NOT block API access. The
// new RPCs override `current_firm_id()` to return NULL for suspended
// or soft-deleted firms, which fail-closes every RLS policy in the
// database simultaneously. The RPCs also write to `firm_audit_log` so
// every state change is traceable.
//
// The old `unsuspendFirm` always reset to 'active' regardless of trial
// state — that quietly extended trials for free. The new sa_unsuspend
// restores the firm to whatever status (trial / active) it would have
// had based on trial_ends_at.
//
// `deleteFirmAdmin` was a hard cascade delete with no recovery. Now
// `softDeleteFirm` marks the firm and preserves data for 30 days; an
// explicit two-step `purgeFirm(id, name)` actually wipes it after that.
export const suspendFirm = (firmId, reason = '') =>
  supabase.rpc('sa_suspend_firm', { p_firm_id: firmId, p_reason: reason || null });

export const unsuspendFirm = (firmId) =>
  supabase.rpc('sa_unsuspend_firm', { p_firm_id: firmId });

export const softDeleteFirm = (firmId, reason = '') =>
  supabase.rpc('sa_soft_delete_firm', { p_firm_id: firmId, p_reason: reason || null });

export const restoreFirm = (firmId) =>
  supabase.rpc('sa_restore_firm', { p_firm_id: firmId });

// Two-factor: the caller MUST pass the firm's exact name as
// confirmation, AND the firm must already be soft-deleted. The RPC
// raises an exception otherwise. Returns { user_ids_to_clean } — pass
// that list to the sa-purge-firm-users Edge Function to delete the
// orphaned auth.users rows.
export const purgeFirm = (firmId, confirmName) =>
  supabase.rpc('sa_purge_firm', { p_firm_id: firmId, p_confirm_name: confirmName });

export const listFirmsDueForPurge = () =>
  supabase.rpc('sa_list_firms_due_for_purge');

// Firm-side self-close (migration 133). Only callable by users with
// role='Admin' on their own firm. Two-factor: the caller types the
// firm's name as confirmation, AND the server checks their role.
// Goes through the same soft-delete machinery as super-admin delete
// — 30-day retention, then eligible for purge. A fat-finger from
// the firm's Admin is recoverable by support during that window.
export const closeMyFirm = (confirmName, reason = '') =>
  supabase.rpc('close_my_firm', {
    p_confirm_name: confirmName,
    p_reason:       reason || null,
  });

// Legacy alias — kept for back-compat with any callers still using
// the old hard-delete name. Routes to soft-delete; the old behaviour
// (immediate hard delete) is now impossible without an explicit
// confirmation, which is correct.
export const deleteFirmAdmin = softDeleteFirm;

// ---------------- super-admin management -----------------
export const saExtendFirmTrial    = (firmId, days) => supabase.rpc('sa_extend_firm_trial', { p_firm_id: firmId, p_days: days });
export const saSetFirmPlan        = (firmId, plan) => supabase.rpc('sa_set_firm_plan',     { p_firm_id: firmId, p_plan: plan });

// Capacity controls — used by the super-admin firm-detail page when a
// customer changes plan or buys an add-on. All three are super-admin
// gated server-side (migrations 141 + 143) so a firm Admin cannot
// raise their own caps even if these helpers were somehow exposed.
export const saSetFirmSeatLimit     = (firmId, limit) =>
  supabase.rpc('set_firm_seat_limit',     { p_firm_id: firmId, p_seat_limit: limit });

export const saSetFirmStorageLimit  = (firmId, limitMb) =>
  supabase.rpc('set_firm_storage_limit',  { p_firm_id: firmId, p_limit_mb:   limitMb });

export const saAddFirmStorageAddon  = (firmId, extraMb) =>
  supabase.rpc('add_firm_storage_addon',  { p_firm_id: firmId, p_extra_mb:   extraMb });
export const saListSuperAdmins    = ()             => supabase.rpc('sa_list_super_admins');
export const saSearchUsers        = (query)        => supabase.rpc('sa_search_users',      { p_query: query });
export const saPromoteSuperAdmin  = (userId)       => supabase.rpc('sa_promote_super_admin', { p_user_id: userId });
export const saDemoteSuperAdmin   = (userId)       => supabase.rpc('sa_demote_super_admin',  { p_user_id: userId });
export const saSystemActivity     = (limit = 100, offset = 0) =>
  supabase.rpc('sa_system_activity', { p_limit: limit, p_offset: offset });

export const saResetFirmAdminPassword = (userId) =>
  supabase.functions.invoke('sa-reset-firm-admin-password', {
    body: { user_id: userId, redirect_to: `${window.location.origin}/reset-password` },
  });

// ---------------- announcements -----------------
export const listMyAnnouncements   = ()                           => supabase.rpc('list_my_announcements');
export const dismissAnnouncement   = (id)                         => supabase.rpc('dismiss_announcement', { p_announcement_id: id });
export const saListAnnouncements   = ()                           => supabase.rpc('sa_list_announcements');
export const saCreateAnnouncement  = (payload)                    => supabase.rpc('sa_create_announcement', {
  p_title:        payload.title,
  p_body:         payload.body || null,
  p_severity:     payload.severity || 'info',
  p_audience:     payload.audience || 'all',
  p_firm_id:      payload.firm_id  || null,
  p_expires_at:   payload.expires_at || null,
  // null = let the DB pick a severity-aware default ('persistent' for info,
  // 'session' for warning, 'none' for critical).
  p_dismiss_mode: payload.dismiss_mode || null,
});
export const saDeleteAnnouncement  = (id)                         => supabase.rpc('sa_delete_announcement', { p_id: id });

// Edit an existing announcement in place — preserves the row id, the
// created_at timestamp, and the dismissals count (which sa_delete_+
// sa_create would all reset). Pass only the fields you want to change;
// the RPC patches the rest with their current values.
//
// To CLEAR a nullable field (e.g. set expires_at back to "never"),
// list its column name in `clear` — the RPC distinguishes between
// "omitted, keep current" and "explicitly clear".
export const saUpdateAnnouncement = (id, payload, clear = []) =>
  supabase.rpc('sa_update_announcement', {
    p_id:           id,
    p_title:        payload.title        ?? null,
    p_body:         payload.body         ?? null,
    p_severity:     payload.severity     ?? null,
    p_audience:     payload.audience     ?? null,
    p_firm_id:      payload.firm_id      ?? null,
    p_expires_at:   payload.expires_at   ?? null,
    p_dismiss_mode: payload.dismiss_mode ?? null,
    p_clear_fields: clear,
  });

// Super-admin: deployment health check. Returns one row per check with
// status 'ok' | 'missing' | 'warn' so the Health page can render a
// green/red dashboard of what's actually applied vs what's expected.
// Surfaces the most common "did I forget to paste a migration?" foot-gun.
export const healthCheckDeployment = () =>
  supabase.rpc('health_check_deployment');

// Sibling RPC for migrations that landed AFTER mig 153 — keeps the
// long, stable health_check_deployment() body untouched. The
// SuperAdminHealthPage calls both and concatenates the results.
// See supabase/migrations/161_health_check_supplementary.sql.
export const healthCheckSupplementary = () =>
  supabase.rpc('health_check_supplementary');

// Schema-migrations ledger (mig 162). Returns one row per applied
// migration. The health page diffs this against migrationsManifest.js
// to compute missing migrations — 100% coverage going forward, no
// per-migration probe drift.
export const listAppliedMigrations = () =>
  supabase.rpc('list_applied_migrations');

// ---------------- MRR snapshots -----------------
export const saRecordMrrSnapshot   = (source = 'manual')          => supabase.rpc('sa_record_mrr_snapshot', { p_source: source });
export const saListMrrSnapshots    = (days = 90)                  => supabase.rpc('sa_list_mrr_snapshots',  { p_days: days });

// ---------------- notifications outbox -----------------
export const saListOutbox          = (status = null, limit = 200) =>
  supabase.rpc('sa_list_outbox', { p_status: status, p_limit: limit });
export const sendEmailNotification = (id = null)                  =>
  supabase.functions.invoke('send-email-notification', { body: id ? { id } : {} });

// ---------------- bulk firm actions -----------------
export const saBulkFirmAction = (firmIds, action, payload = {}) =>
  supabase.rpc('sa_bulk_firm_action', { p_firm_ids: firmIds, p_action: action, p_payload: payload });

// ---------------- access log (security audit) -----------------
// Fire-and-forget — never await; logging failures should never block the UI.
export const logAccess = (action, metadata = {}) => {
  (async () => {
    try {
      await supabase.rpc('log_access', {
        p_action:     action,
        p_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        p_metadata:   metadata,
      });
    } catch (_) { /* swallow */ }
  })();
};

export const listAccessLog = (firmId, limit = 100) =>
  withTimeout(
    from('access_log')
      .select('id, action, user_email, user_agent, ip_address, metadata, created_at, user:users(id, first_name, last_name)')
      .eq('firm_id', firmId)
      .order('created_at', { ascending: false })
      .limit(limit),
    QUERY_TIMEOUT
  );

// ---------------- super-admin impersonation -----------------
// Server-side state — current_firm_id() reads from super_admin_impersonations
// so RLS treats the super-admin as a member of the impersonated firm.
export const startImpersonation     = (firmId) => supabase.rpc('start_impersonation', { p_firm_id: firmId });
export const endImpersonation       = ()       => supabase.rpc('end_impersonation');
export const getActiveImpersonation = ()       => supabase.rpc('get_active_impersonation');

// ---------------- roles & permissions -----------------
export const listFirmRoles = (firmId) =>
  withTimeout(
    from('firm_roles')
      .select('*')
      .eq('firm_id', firmId)
      .order('created_at', { ascending: true }),
    QUERY_TIMEOUT
  );

// Re-create the seven system roles (Admin/Partner/Attorney/Associate/
// Paralegal/Clerk + their permission grants) for the caller's firm. Used
// when a firm signed up before the auto-seed trigger existed (migration
// 071) and ended up with an empty Roles page. Idempotent on the RPC side
// (ON CONFLICT DO NOTHING).
export const seedDefaultRoles = (firmId) =>
  supabase.rpc('seed_default_roles', { p_firm_id: firmId });

export const createFirmRole = (firmId, payload) =>
  from('firm_roles')
    .insert({ firm_id: firmId, ...payload })
    .select()
    .single();

export const updateFirmRole = (roleId, payload) =>
  from('firm_roles').update(payload).eq('id', roleId).select().single();

export const deleteFirmRole = (roleId) =>
  from('firm_roles').delete().eq('id', roleId).eq('is_system', false);

export const listRolePermissions = (roleId) =>
  from('firm_role_permissions').select('permission').eq('role_id', roleId);

export const addRolePermission = (roleId, permission) =>
  from('firm_role_permissions').insert({ role_id: roleId, permission });

export const removeRolePermission = (roleId, permission) =>
  from('firm_role_permissions')
    .delete()
    .eq('role_id', roleId)
    .eq('permission', permission);

export const setUserRole = (userId, roleId) =>
  from('users').update({ role_id: roleId }).eq('id', userId);

// ---------------- firms & user profile -----------------
export const fetchMyProfile = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: null };
  const { data, error } = await from('users').select('*').eq('id', user.id).maybeSingle();
  return { data, error };
};

export const fetchFirm = async (firmId) => {
  if (!firmId) return { data: null, error: null };
  // Use the SECURITY DEFINER RPC so the firm always loads regardless of the
  // `own_firm` RLS SELECT policy state (which can block during account repair).
  // Falls back to a direct table query if the RPC doesn't exist yet.
  const { data: rpcData, error: rpcErr } = await supabase.rpc('get_my_firm');
  if (!rpcErr && rpcData?.length > 0) return { data: rpcData[0], error: null };
  // Fallback: direct query (requires RLS to be correctly configured)
  return from('firms').select('*').eq('id', firmId).maybeSingle();
};

export const updateFirm = (firmId, payload) =>
  from('firms').update(payload).eq('id', firmId).select().maybeSingle();

// Per-user "I've seen this onboarding step" flag. Backed by
// public.users.welcome_seen_at / tour_seen_at (migration 126). Survives
// localStorage clears, redeploys, and cross-device logins.
//
// Returns a real Promise (not the PostgrestBuilder thenable) so callers
// can use `.catch()` for fire-and-forget without worrying about which
// SDK version exposes .catch on the builder.
export const markOnboardingStepSeen = (step) =>
  Promise.resolve(supabase.rpc('mark_onboarding_step_seen', { p_step: step }));

// Auto-create the caller's employees row if missing (migration 127).
// Used for firm owners + legacy users who pre-date the auto-create
// trigger. Idempotent — if a row already exists, returns it as-is.
export const ensureMyEmployeeRecord = () =>
  Promise.resolve(supabase.rpc('ensure_my_employee_record'));

// Sample data — load or wipe demo content for a fresh firm. Both go through
// SECURITY DEFINER RPCs (migration 117) so they're safe to call from the
// browser and idempotent.
export const loadSampleData = () => supabase.rpc('load_sample_data');
export const wipeSampleData = () => supabase.rpc('wipe_sample_data');

// Practice-area starter pack — pre-create document folders matched to the
// firm's selected practice areas (migration 118). Idempotent on the firm
// so re-running is safe and won't duplicate folders.
export const seedStarterPack = (practiceAreas = []) =>
  supabase.rpc('seed_starter_pack', { p_practice_areas: practiceAreas });

// Onboarding wizard finish step. Uses a SECURITY DEFINER RPC (migration 115)
// to atomically flip setup_completed + save the wizard's payload without
// going through the firms_update + firms_select RLS pair, which can return
// zero rows on a freshly-created session before its cache catches up.
// Returns { data, error } with the same shape as a PostgREST update.
export const completeFirmSetup = (payload) =>
  supabase.rpc('complete_firm_setup', { p_patch: payload });

export const joinFirmByInviteCode = (code) =>
  supabase.rpc('join_firm_by_invite_code', { code });

export const ensureFirmForUser = async (firmName = 'My Firm') => {
  // If the logged-in user has no firm_id yet, create a firm and link them.
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: { message: 'Not logged in' } };

  const { data: prof } = await from('users').select('firm_id').eq('id', user.id).maybeSingle();
  if (prof?.firm_id) return { data: prof, error: null };

  // If an invite code is waiting in sessionStorage, join that firm instead of
  // creating a new one. The RPC handles auth and links the user atomically.
  const inviteCode = sessionStorage.getItem('inviteCode');
  if (inviteCode) {
    const { data, error } = await joinFirmByInviteCode(inviteCode);
    if (!error) sessionStorage.removeItem('inviteCode');
    return { data, error };
  }

  // Atomic firm creation via SECURITY DEFINER RPC (migration 114). One DB
  // transaction creates the firm, fires default-role + default-channel
  // triggers, and links the user. RLS doesn't apply (SECURITY DEFINER), so
  // we avoid the read-back / visibility races that the earlier multi-step
  // client-side flow ran into in different environments.
  const { data: rpcRes, error: rpcErr } = await supabase.rpc('create_firm_for_user', {
    p_firm_name: firmName,
  });
  if (rpcErr) {
    // Fall back to the legacy multi-step flow if the RPC isn't present yet
    // (older DBs without migration 114 applied). This preserves backwards
    // compatibility while users roll their migrations forward.
    const looksMissing =
      /function\s+public\.create_firm_for_user|404|404|PGRST202|Could not find the function/i.test(
        rpcErr.message || ''
      );
    if (!looksMissing) return { data: null, error: rpcErr };

    // Legacy path — only reached on out-of-date DBs.
    const firmId = crypto.randomUUID();
    const { error: firmErr } = await from('firms')
      .insert({ id: firmId, name: firmName, email: user.email });
    if (firmErr) return { data: null, error: firmErr };
    await supabase.rpc('seed_default_roles', { p_firm_id: firmId });
    if (!prof) {
      const { data: inserted, error: insErr } = await from('users')
        .insert({ id: user.id, email: user.email, firm_id: firmId, role: 'Admin', status: 'Active' })
        .select()
        .single();
      return { data: inserted, error: insErr };
    }
    const { data: updated, error: updErr } = await from('users')
      .update({ firm_id: firmId, role: 'Admin', status: 'Active' })
      .eq('id', user.id)
      .select()
      .single();
    return { data: updated, error: updErr };
  }

  return { data: { firm_id: rpcRes?.firm_id }, error: null };
};

// ---------------- seat enforcement -----------------
// Returns the firm's seat usage so the UI can show
// "X of Y seats used" and disable invite when at cap.
//
// Shape: { data: { firm_id, seat_limit, in_use, available, is_full } | null, error }
// seat_limit === null means unlimited (enterprise plan or no cap set).
export const getMyFirmSeatInfo = async () => {
  const { data, error } = await supabase.rpc('get_my_firm_seat_info');
  if (error) {
    if (error.code === 'PGRST202') {
      // Migration 141 not deployed yet — degrade gracefully so the
      // Settings page doesn't crash. Returning null means "no
      // enforcement information available; behave like unlimited."
      return { data: null, error: null };
    }
    return { data: null, error };
  }
  // The function returns SETOF — pick the first row.
  const row = Array.isArray(data) ? data[0] : data;
  return { data: row || null, error: null };
};

// ---------------- storage enforcement -----------------
// Returns the firm's storage usage so the UI can show
// "X GB of Y GB used", disable upload at cap, and prompt to buy an
// add-on when near full.
//
// Shape: { data: { firm_id, used_mb, limit_mb, addon_mb, available_mb,
//                  is_full, is_near_full } | null, error }
// limit_mb === null means unlimited (enterprise plan).
//
// Degrades to null on PGRST202 (migration 143 not deployed) so older
// environments don't crash.
export const getMyFirmStorageInfo = async () => {
  const { data, error } = await supabase.rpc('get_my_firm_storage_info');
  if (error) {
    if (error.code === 'PGRST202') return { data: null, error: null };
    return { data: null, error };
  }
  const row = Array.isArray(data) ? data[0] : data;
  return { data: row || null, error: null };
};

// Map the trigger's 54000 / "Storage limit reached" error into a
// stable { code: 'STORAGE_LIMIT' } shape the UI can match on. Mirrors
// the SEAT_LIMIT mapping in inviteTeamMember above.
//
// Call this on any error returned from a document INSERT path
// (uploadDocumentFile, createDocument, etc.) before surfacing to UI.
export const mapStorageLimitError = (error) => {
  if (!error) return error;
  const msg  = error.message || '';
  const code = error.code    || '';
  if (code === '54000' || /storage\s+limit\s+reached/i.test(msg)) {
    return {
      ...error,
      code: 'STORAGE_LIMIT',
      message: msg.replace(/^.*?storage\s+limit\s+reached/i, 'Storage limit reached'),
    };
  }
  return error;
};

// ---------------- team / users -----------------
//
// Preferred: SECURITY DEFINER RPC (migration 138) that resolves firm_id
// server-side from auth.uid() → public.users. Fixes the failure mode
// where the public.users SELECT RLS (migration 023, predicate
// `firm_id::uuid = current_firm_id()`) returns zero rows because
// current_firm_id() has drifted from the user's actual firm_id.
//
// Symptom this fixes: "New direct message → No other team members
// found." even though the firm has multiple employees. Also affects
// Settings → Team, Calendar assignee picker, MatterDetail team picker,
// Documents share-with picker, Dashboard team widget.
//
// Falls back to the original RLS-bound query on older DBs without the
// RPC.
export const listFirmUsers = async (firmId) => {
  const rpcRes = await supabase.rpc('list_my_firm_users');
  if (!rpcRes.error) {
    return { data: Array.isArray(rpcRes.data) ? rpcRes.data : [], error: null };
  }
  const looksMissing = /PGRST202|Could not find the function|function .*list_my_firm_users/i.test(
    rpcRes.error.message || '',
  );
  if (!looksMissing) return { data: null, error: rpcRes.error };

  // Legacy fallback — direct query, RLS-bound. Will only succeed if
  // current_firm_id() matches the supplied firmId.
  return withTimeout(
    from('users')
      .select('id, first_name, last_name, email, role, status, last_login, created_at, email_confirmed')
      .eq('firm_id', firmId)
      .order('first_name', { ascending: true }),
    QUERY_TIMEOUT
  );
};

export const updateUserRole = (userId, role) =>
  from('users').update({ role }).eq('id', userId).select().single();

export const updateUserStatus = (userId, status) =>
  from('users').update({ status }).eq('id', userId).select().single();

// ---------------- demo requests (marketing site lead capture) -----------------
//
// Submits via the `submit_demo_request` SECURITY DEFINER RPC (migration 159)
// instead of a direct INSERT on public.demo_requests. Why:
//
//   * Direct INSERT requires RLS policy + table-level GRANT to anon, both
//     correctly installed. In real Supabase projects we've seen the GRANT
//     fall out of sync silently (Supabase's newer sb_publishable_xxx key
//     format, project-level RLS toggles, REVOKE chains, etc.) — producing
//     "permission denied for table demo_requests" 42501 errors for visitors
//     even when migration 154 installed the policy correctly.
//   * The RPC runs SECURITY DEFINER (as postgres), bypassing both RLS and
//     table grants. The only thing that matters is the EXECUTE grant on
//     the function, which is much harder to accidentally break.
//   * Same validation rules (required fields, email regex, message length)
//     are enforced inside the function body, so spam mitigation is
//     unchanged.
//
// Returns { data, error } where `data` is the new row's UUID. Caller still
// just checks `error` to decide success/failure UI.
export const submitDemoRequest = async (payload) => {
  const userAgent = ((typeof navigator !== 'undefined' && navigator.userAgent) || '').slice(0, 500) || null;
  const referrer  = ((typeof document  !== 'undefined' && document.referrer)    || '').slice(0, 500) || null;
  return supabase.rpc('submit_demo_request', {
    p_full_name:     (payload.fullName     || '').trim(),
    p_email:         (payload.email        || '').trim(),
    p_firm_name:     (payload.firmName     || '').trim(),
    p_phone:         (payload.phone        || '').trim() || null,
    p_firm_size:     payload.firmSize      || null,
    p_role:          payload.role          || null,
    p_practice_area: (payload.practiceArea || '').trim() || null,
    p_demo_time:     payload.preferredTime || null,
    p_message:       (payload.message      || '').trim() || null,
    p_user_agent:    userAgent,
    p_referrer:      referrer,
  });
};

// Super-admin: list pending / historical demo requests.
export const listDemoRequests = (opts = {}) =>
  supabase.rpc('list_demo_requests', {
    p_status: opts.status || null,
    p_limit:  opts.limit  || 100,
  });

// Super-admin: change a demo request's status (new → contacted → ... → closed).
export const updateDemoRequestStatus = (id, status) =>
  supabase.rpc('update_demo_request_status', { p_id: id, p_status: status });

// ---------------- demo-view tracking (analytics for the in-page demo modal) -----------------
//
// Fire-and-forget event tracker. Called from the WatchDemoModal when:
//   * the modal opens          → kind 'opened'
//   * a new scene becomes visible → kind 'scene_viewed' + sceneIndex
//   * the modal closes         → kind 'closed' + dwellMs
//
// We don't await the result — analytics are non-critical and shouldn't
// block the UI. We also don't surface errors to the user — a failed
// insert is a missing analytics row, not a failed visitor experience.
// All errors are logged to console.debug for diagnosis.
//
// The session_id is generated client-side once per modal open. All
// events from one modal session share that id so we can group/dedupe
// in the SuperAdmin aggregate queries.
export const trackDemoEvent = (sessionId, eventType, { sceneIndex, dwellMs } = {}) => {
  // Build the row defensively — RLS CHECK enforces the right shape,
  // but we want clean fire-and-forget semantics here.
  const row = {
    session_id:  sessionId,
    event_type:  eventType,
    scene_index: eventType === 'scene_viewed' ? sceneIndex : null,
    dwell_ms:    eventType === 'closed'       ? dwellMs    : null,
    referrer:    ((typeof document  !== 'undefined' && document.referrer)    || '').slice(0, 500) || null,
    user_agent:  ((typeof navigator !== 'undefined' && navigator.userAgent) || '').slice(0, 500) || null,
  };
  // Fire and forget. Catch any error so an offline visitor doesn't get
  // an unhandled-rejection warning in the console.
  return from('demo_view_events').insert(row).then(
    (res) => {
      if (res.error && process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[demoView] event insert failed:', res.error);
      }
      return res;
    },
    (err) => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[demoView] event insert threw:', err);
      }
      return { data: null, error: err };
    },
  );
};

// Super-admin: aggregate stats for the SuperAdmin demo-views page.
export const getDemoViewStats = (days = 30) =>
  supabase.rpc('get_demo_view_stats', { p_days: days });

// Super-admin: recent sessions feed (one row per session).
export const listRecentDemoViews = (limit = 50) =>
  supabase.rpc('list_recent_demo_views', { p_limit: limit });

// ---------------- contacts -----------------
export const listContacts = (firmId, type = null, opts = {}) => {
  let q = from('contacts').select('*').eq('firm_id', firmId).order('last_name', { ascending: true });
  if (type) q = q.eq('contact_type', type);
  q = q.limit(opts.limit ?? 500);
  return withTimeout(q, QUERY_TIMEOUT);
};

export const createContact = (firmId, payload) => {
  if (!firmId) {
    return Promise.resolve({
      data: null,
      error: { message: 'Cannot create contact: firm not loaded yet. Please refresh and try again.' },
    });
  }
  return from('contacts').insert({ firm_id: firmId, ...payload }).select().single();
};

export const fetchContact = (id) =>
  from('contacts').select('*').eq('id', id).maybeSingle();

export const updateContact = (id, payload) =>
  from('contacts').update(payload).eq('id', id).select().single();

export const deleteContact = (id) => from('contacts').delete().eq('id', id);

// ---------------- matters -----------------
export const listMatters = (firmId, filters = {}) => {
  let q = from('matters')
    // Only pull the client columns we render, not the whole row.
    .select('*, client:contacts!matters_client_id_fkey(id,first_name,last_name,organization)')
    .eq('firm_id', firmId)
    .order('created_at', { ascending: false });
  if (filters.status) q = q.eq('status', filters.status);
  if (filters.attorney) q = q.eq('responsible_attorney_id', filters.attorney);
  if (filters.practiceArea) q = q.eq('practice_area', filters.practiceArea);
  q = q.limit(filters.limit ?? 200);
  return withTimeout(q, QUERY_TIMEOUT);
};

export const getMatter = (id) =>
  from('matters').select('*, client:contacts!matters_client_id_fkey(*)').eq('id', id).maybeSingle();

export const createMatter = (firmId, payload) =>
  from('matters').insert({ firm_id: firmId, ...payload }).select().single();

export const updateMatter = (id, payload) =>
  from('matters').update(payload).eq('id', id).select().single();

// Toggle a matter's confidentiality flag. When ON, only Admin/Partner +
// responsible attorney + assigned matter_users/matter_team can see the
// matter and its artifacts (documents, tasks, time entries, notes,
// deadlines, invoices, case charges). Routes through migration 140's
// SECURITY DEFINER RPC which:
//   * authorises the caller (leader or responsible attorney or assigned)
//   * auto-adds the caller to matter_users when flipping ON, so they
//     don't lock themselves out
//   * applies the firm-scope check server-side
//
// Returns { data: boolean (the new flag value), error }.
export const setMatterConfidential = async (matterId, confidential) => {
  const { data, error } = await supabase.rpc('set_matter_confidential', {
    p_matter_id:    matterId,
    p_confidential: !!confidential,
  });
  return { data, error };
};

// Close a matter: set status=closed, record closed_at + closing_reason + close_date
export const closeMatter = (id, { closing_reason = '', close_date } = {}) => {
  const now = new Date().toISOString();
  return from('matters').update({
    status: 'closed',
    closed_at: now,
    closing_reason: closing_reason || null,
    close_date: close_date || now.slice(0, 10),
  }).eq('id', id).select().single();
};

// Archive a matter: set status=archived + archived_at (must already be closed)
export const archiveMatter = (id) =>
  from('matters').update({
    status: 'archived',
    archived_at: new Date().toISOString(),
  }).eq('id', id).select().single();

// Reopen a matter: clear closed/archived fields, restore status to open
export const reopenMatter = (id) =>
  from('matters').update({
    status: 'open',
    closed_at: null,
    closing_reason: null,
    archived_at: null,
    close_date: null,
  }).eq('id', id).select().single();

// ---------------- matter notes -----------------
export const listMatterNotes = (matterId) =>
  withTimeout(
    from('matter_notes')
      .select('*, user:users(first_name, last_name)')
      .eq('matter_id', matterId)
      .order('note_date', { ascending: false })
      .order('created_at', { ascending: false }),
    QUERY_TIMEOUT
  );

export const createMatterNote = (firmId, payload) =>
  from('matter_notes').insert({ firm_id: firmId, ...payload }).select('*, user:users(first_name, last_name)').single();

export const updateMatterNote = (id, payload) =>
  from('matter_notes').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', id).select('*, user:users(first_name, last_name)').single();

export const deleteMatterNote = (id) =>
  from('matter_notes').delete().eq('id', id);

// ---------------- events & deadlines -----------------
export const listEvents = (firmId, rangeStart, rangeEnd) => {
  let q = from('events').select('*').eq('firm_id', firmId).order('start_at', { ascending: true });
  // Fetch any event that overlaps the range:
  //   event starts before range ends  AND  (event ends after range starts OR has no end)
  if (rangeStart) q = q.or(`end_at.gte.${rangeStart},end_at.is.null,start_at.gte.${rangeStart}`);
  if (rangeEnd)   q = q.lte('start_at', rangeEnd);
  return withTimeout(q, QUERY_TIMEOUT);
};

export const createEvent = (firmId, payload) =>
  from('events').insert({ firm_id: firmId, ...payload }).select().single();

export const updateEvent = (id, payload) =>
  from('events').update(payload).eq('id', id).select().single();

export const deleteEvent = (id) => from('events').delete().eq('id', id);

export const listDeadlines = (firmId, openOnly = true) => {
  let q = from('deadlines').select('*, matter:matters(id,title,matter_number)')
    .eq('firm_id', firmId).order('due_date', { ascending: true });
  if (openOnly) q = q.eq('status', 'open');
  return withTimeout(q, QUERY_TIMEOUT);
};

export const createDeadline = (firmId, payload) =>
  from('deadlines').insert({ firm_id: firmId, ...payload }).select().single();

export const updateDeadline = (id, payload) =>
  from('deadlines').update(payload).eq('id', id).select().single();

export const deleteDeadline = (id) => from('deadlines').delete().eq('id', id);

// ---------------- time entries -----------------
export const listTimeEntries = (firmId, opts = {}) => {
  let q = from('time_entries').select('*, matter:matters(id,title,matter_number)')
    .eq('firm_id', firmId).order('entry_date', { ascending: false });
  if (opts.matterId) q = q.eq('matter_id', opts.matterId);
  if (opts.userId)   q = q.eq('user_id', opts.userId);
  if (opts.since)    q = q.gte('entry_date', opts.since);
  // Default cap so the dashboard never pulls the whole table.
  q = q.limit(opts.limit ?? 500);
  return withTimeout(q, QUERY_TIMEOUT);
};

export const createTimeEntry = (firmId, payload) =>
  from('time_entries').insert({ firm_id: firmId, ...payload }).select().single();

export const deleteTimeEntry = (id) =>
  from('time_entries').delete().eq('id', id);

// ---------------- invoices -----------------
export const listInvoices = (firmId, opts = {}) => {
  let q = from('invoices')
    .select('*, client:contacts(id,first_name,last_name,organization)')
    .eq('firm_id', firmId)
    .order('issue_date', { ascending: false })
    .limit(opts.limit ?? 200);
  if (opts.matterId) q = q.eq('matter_id', opts.matterId);
  return withTimeout(q, QUERY_TIMEOUT);
};

export const getInvoice = (id) =>
  from('invoices').select('*, client:contacts(*), line_items:invoice_line_items(*)').eq('id', id).maybeSingle();

/** Public invoice lookup — uses the anon role via the SECURITY DEFINER RPC.
 *  No authentication required; token is a random UUID so it's unguessable. */
export const getInvoiceByShareToken = (token) =>
  supabase.rpc('get_invoice_by_share_token', { p_token: token });

export const createInvoice = (firmId, payload) =>
  from('invoices').insert({ firm_id: firmId, ...payload }).select().single();

export const updateInvoice = (id, payload) =>
  from('invoices').update(payload).eq('id', id).select().single();

export const deleteInvoice = (id) =>
  from('invoices').delete().eq('id', id);

export const createInvoiceLineItems = (items) =>
  from('invoice_line_items').insert(items).select();

export const deleteInvoiceLineItems = (invoiceId) =>
  from('invoice_line_items').delete().eq('invoice_id', invoiceId);

// ---------------- case billing -----------------
/** Update a matter's agreed fee & fee arrangement */
export const updateMatterBilling = (matterId, payload) =>
  from('matters').update(payload).eq('id', matterId).select().single();

/**
 * Record an installment payment against a matter.
 *
 * IDEMPOTENCY CONTRACT (migration 134):
 *   The caller SHOULD pass `payload.idempotency_key` — a UUID generated
 *   ONCE per user intent (e.g. when the payment form opens). The same
 *   key on a retry returns the original row via the partial UNIQUE
 *   index on `(firm_id, idempotency_key)`. Without the key, the call
 *   still works but loses dedup protection — a network retry could
 *   create a duplicate payment.
 *
 *   We deliberately do NOT auto-generate the key in this helper:
 *   auto-generation defeats the entire purpose, because each retry
 *   would get a fresh UUID and slip past the dedup. The key has to be
 *   stable across retries, which only the caller knows how to do.
 *
 *   On collision Postgres raises code 23505 (unique_violation). The
 *   caller can fetch the existing row by idempotency_key to recover.
 */
export const recordInstallmentPayment = (firmId, payload) =>
  from('payments').insert({ firm_id: firmId, ...payload }).select().single();

/**
 * Look up a payment by its idempotency key. Used by the duplicate-
 * recovery path: after a 23505, the client calls this to get the
 * row that DID land, then surfaces it as success.
 */
export const fetchPaymentByIdempotencyKey = (firmId, key) =>
  from('payments')
    .select('*')
    .eq('firm_id', firmId)
    .eq('idempotency_key', key)
    .maybeSingle();

/** List all payments for a matter (installments) */
export const listMatterPayments = (matterId) =>
  from('payments')
    .select('*')
    .eq('matter_id', matterId)
    .order('payment_date', { ascending: false });

/** List ALL matter-linked payments for a firm (for billing dashboard) */
export const listAllMatterPayments = (firmId) =>
  from('payments')
    .select('id,matter_id,amount,payment_date,method,payment_label')
    .eq('firm_id', firmId)
    .not('matter_id', 'is', null)
    .limit(1000);

/** Delete a payment record */
export const deletePayment = (id) =>
  from('payments').delete().eq('id', id);

/** Add an extra charge to a case */
export const addCaseCharge = (firmId, payload) =>
  from('case_charges').insert({ firm_id: firmId, ...payload }).select().single();

/** List extra charges for a matter */
export const listCaseCharges = (matterId) =>
  from('case_charges')
    .select('*')
    .eq('matter_id', matterId)
    .order('charge_date', { ascending: false });

/** List ALL case charges for a firm (for billing dashboard) */
export const listAllCaseCharges = (firmId) =>
  from('case_charges')
    .select('id,matter_id,amount,charge_date,charge_type,description')
    .eq('firm_id', firmId)
    .limit(1000);

/** Update an extra charge */
export const updateCaseCharge = (id, payload) =>
  from('case_charges').update(payload).eq('id', id).select().single();

/** Mark a charge as paid (or unpaid) */
export const markCaseChargePaid = (id, paid = true) =>
  from('case_charges')
    .update({ status: paid ? 'paid' : 'unpaid', paid_at: paid ? new Date().toISOString() : null })
    .eq('id', id).select().single();

/** Delete an extra charge */
export const deleteCaseCharge = (id) =>
  from('case_charges').delete().eq('id', id);

// ---------------- trust -----------------
export const listTrustAccounts = (firmId) =>
  from('trust_accounts').select('*').eq('firm_id', firmId).order('created_at', { ascending: true });

export const createTrustAccount = (firmId, payload) =>
  from('trust_accounts').insert({ firm_id: firmId, ...payload }).select().single();

export const updateTrustAccount = (id, payload) =>
  from('trust_accounts').update(payload).eq('id', id).select().single();

export const deleteTrustAccount = (id) =>
  from('trust_accounts').delete().eq('id', id);

export const listTrustTransactions = (firmId, opts = {}) => {
  let q = from('trust_transactions')
    .select('*, client:contacts(*), matter:matters(id,title,matter_number), account:trust_accounts(id,name)')
    .eq('firm_id', firmId).order('transaction_date', { ascending: false });
  if (opts.clientId)  q = q.eq('client_id', opts.clientId);
  if (opts.matterId)  q = q.eq('matter_id', opts.matterId);
  if (opts.accountId) q = q.eq('account_id', opts.accountId);
  // Default cap — prevents unbounded scans on large ledgers.
  q = q.limit(opts.limit ?? 500);
  return q;
};

export const createTrustTransaction = (firmId, payload) =>
  from('trust_transactions').insert({ firm_id: firmId, ...payload }).select().single();

export const clientTrustBalances = async (firmId) => {
  // Aggregate client trust balances client-side.
  // No join needed — the Dashboard only uses the `total` and `count` fields,
  // not client names. Keeping the select minimal avoids the FK-ambiguity 400
  // that PostgREST throws when there's no explicit hint on the contacts join.
  const { data, error } = await from('trust_transactions')
    .select('client_id, transaction_type, amount')
    .eq('firm_id', firmId)
    .limit(2000);
  if (error) return { data: [], error };
  const byClient = new Map();
  for (const tx of data || []) {
    if (!tx.client_id) continue;
    const sign = tx.transaction_type === 'deposit' ? 1 : -1;
    const prev = byClient.get(tx.client_id) || { client_id: tx.client_id, total: 0, count: 0 };
    prev.total += sign * Number(tx.amount);
    prev.count += 1;
    byClient.set(tx.client_id, prev);
  }
  return { data: Array.from(byClient.values()), error: null };
};

export const listTrustReconciliations = (firmId, accountId = null) => {
  let q = from('trust_reconciliations').select('*').eq('firm_id', firmId).order('as_of_date', { ascending: false });
  if (accountId) q = q.eq('account_id', accountId);
  return q;
};

export const createTrustReconciliation = (firmId, payload) =>
  from('trust_reconciliations').insert({ firm_id: firmId, ...payload }).select().single();

// ---------------- intake -----------------
// Remap DB "schema" → UI "fields" on the way out.
const toIntakeForm = (row) => row ? { ...row, fields: row.schema } : row;

export const listIntakeForms = async (firmId) => {
  const res = await from('intake_forms').select('*').eq('firm_id', firmId).order('created_at', { ascending: false });
  return { ...res, data: (res.data || []).map(toIntakeForm) };
};

// The UI calls the field array "fields"; the DB column is "schema".
// Remap here so IntakePage doesn't need to know about the DB naming.
const normaliseIntakePayload = ({ fields, ...rest }) => ({
  ...rest,
  ...(fields !== undefined && { schema: fields }),
});

function generateSlug(name) {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')   // strip special chars
    .replace(/\s+/g, '-')            // spaces → hyphens
    .replace(/-+/g, '-')             // collapse multiple hyphens
    .substring(0, 60)                // max length
    + '-' + Math.random().toString(36).slice(2, 7); // unique suffix
}

export const createIntakeForm = async (firmId, payload) => {
  const normalised = normaliseIntakePayload(payload);
  if (!normalised.slug && normalised.name) {
    normalised.slug = generateSlug(normalised.name);
  }
  const res = await from('intake_forms').insert({ firm_id: firmId, ...normalised }).select().single();
  return { ...res, data: toIntakeForm(res.data) };
};

export const updateIntakeForm = async (id, payload) => {
  const res = await from('intake_forms').update(normaliseIntakePayload(payload)).eq('id', id).select().single();
  return { ...res, data: toIntakeForm(res.data) };
};

export const listIntakeSubmissions = (firmId) =>
  from('intake_submissions').select('*, form:intake_forms(id,name)').eq('firm_id', firmId).order('submitted_at', { ascending: false });

export const createIntakeSubmission = (firmId, payload) =>
  from('intake_submissions').insert({ firm_id: firmId, ...payload }).select().single();

export const updateIntakeSubmission = (id, payload) =>
  from('intake_submissions').update(payload).eq('id', id).select().single();

// Conflict check — searches contacts AND matters for any overlap
export const runConflictCheck = async (firmId, { name, email, phone, organization }) => {
  const hits = [];

  // 1. Email match against contacts
  if (email) {
    const { data } = await from('contacts')
      .select('*, matters:matters!matters_client_id_fkey(id,title,matter_number,status)')
      .eq('firm_id', firmId).ilike('email', email);
    for (const c of data || []) hits.push({ type: 'email', severity: 'high', contact: c, matters: c.matters || [] });
  }

  // 2. Phone match against contacts
  if (phone) {
    const { data } = await from('contacts')
      .select('*, matters:matters!matters_client_id_fkey(id,title,matter_number,status)')
      .eq('firm_id', firmId).ilike('phone', `%${phone.replace(/\s/g, '')}%`);
    for (const c of data || []) {
      if (!hits.find((h) => h.contact?.id === c.id))
        hits.push({ type: 'phone', severity: 'high', contact: c, matters: c.matters || [] });
    }
  }

  // 3. Name match against contacts (first or last name)
  if (name) {
    const parts = name.trim().split(/\s+/);
    for (const part of parts) {
      if (part.length < 2) continue;
      const { data: byFirst } = await from('contacts')
        .select('*, matters:matters!matters_client_id_fkey(id,title,matter_number,status)')
        .eq('firm_id', firmId).ilike('first_name', `%${part}%`);
      const { data: byLast } = await from('contacts')
        .select('*, matters:matters!matters_client_id_fkey(id,title,matter_number,status)')
        .eq('firm_id', firmId).ilike('last_name', `%${part}%`);
      for (const c of [...(byFirst || []), ...(byLast || [])]) {
        if (!hits.find((h) => h.contact?.id === c.id))
          hits.push({ type: 'name', severity: 'medium', contact: c, matters: c.matters || [] });
      }
    }
  }

  // 4. Organization match against contacts
  if (organization) {
    const { data } = await from('contacts')
      .select('*, matters:matters!matters_client_id_fkey(id,title,matter_number,status)')
      .eq('firm_id', firmId).ilike('organization', `%${organization}%`);
    for (const c of data || []) {
      if (!hits.find((h) => h.contact?.id === c.id))
        hits.push({ type: 'organization', severity: 'medium', contact: c, matters: c.matters || [] });
    }
  }

  // 5. Name match against matter titles — uses SECURITY DEFINER RPC so the
  //    search spans ALL firm matters regardless of the caller's role.
  //    This ensures non-admin staff (paralegals, clerks) catch real conflicts.
  if (name) {
    const { data } = await supabase.rpc('search_matters_conflict_check', {
      p_firm_id: firmId,
      p_query:   name,
    });
    for (const m of data || [])
      hits.push({ type: 'matter_title', severity: 'low', matter: m });
  }

  return { data: hits, error: null };
};

// Save conflict check result
export const saveConflictCheck = (firmId, payload) =>
  from('conflict_checks').insert({ firm_id: firmId, ...payload }).select().single();

export const listConflictChecks = (firmId) =>
  withTimeout(
    from('conflict_checks').select('*').eq('firm_id', firmId).order('created_at', { ascending: false }).limit(100),
    QUERY_TIMEOUT
  );

// ---------------- tasks -----------------
export const listTasks = (firmId, opts = {}) => {
  let q = from('tasks').select('*, matter:matters!tasks_matter_id_fkey(id,title,matter_number)').eq('firm_id', firmId).order('due_date', { ascending: true, nullsFirst: false });
  if (opts.assignedTo) q = q.eq('assigned_to', opts.assignedTo);
  if (opts.matterId)   q = q.eq('matter_id', opts.matterId);
  return withTimeout(q, QUERY_TIMEOUT);
};

export const createTask = (firmId, payload) =>
  from('tasks').insert({ firm_id: firmId, ...payload }).select('*, matter:matters!tasks_matter_id_fkey(id,title,matter_number)').single();

export const updateTask = (id, payload) =>
  from('tasks').update(payload).eq('id', id).select('*, matter:matters!tasks_matter_id_fkey(id,title,matter_number)').single();

export const deleteTask = (id) => from('tasks').delete().eq('id', id);

// ---------------- expenses -----------------
export const listExpenses = (firmId, opts = {}) => {
  let q = from('expenses')
    .select('*, matter:matters(id,title,matter_number)')
    .eq('firm_id', firmId)
    .order('expense_date', { ascending: false });
  if (opts.limit) q = q.limit(opts.limit);
  return withTimeout(q, QUERY_TIMEOUT);
};

export const createExpense = (firmId, payload) =>
  from('expenses').insert({ firm_id: firmId, ...payload }).select('*, matter:matters(id,title,matter_number)').single();

export const updateExpense = (id, payload) =>
  from('expenses').update(payload).eq('id', id).select('*, matter:matters(id,title,matter_number)').single();

export const deleteExpense = (id) => from('expenses').delete().eq('id', id);

// ---------------- document folders -----------------
//
// Create / update / delete go through SECURITY DEFINER RPCs (migration
// 136) that resolve firm_id from auth.uid() server-side. The previous
// direct INSERT was failing with "new row violates row-level security
// policy" whenever current_firm_id() drifted from the user's actual
// firm — the same drift pattern that bit messaging and the employee
// profile flow earlier. The RPC bypasses the drifted helper without
// loosening security: it still reads the caller's firm strictly from
// THEIR users row, so cross-firm writes are impossible.
//
// Each helper falls back to a direct query if the RPC isn't deployed
// yet (PGRST202) so we don't break older environments mid-rollout.
export const listFolders = (firmId) =>
  withTimeout(from('document_folders').select('*').eq('firm_id', firmId).order('name'), QUERY_TIMEOUT);

export const createFolder = async (firmId, payload) => {
  const { data, error } = await supabase.rpc('create_my_document_folder', {
    p_name:      payload?.name ?? '',
    p_color:     payload?.color ?? '#6366f1',
    p_parent_id: payload?.parent_id ?? null,
  });
  if (!error) return { data, error: null };
  if (error.code === 'PGRST202') {
    // RPC missing on this DB — fall back to direct insert.
    return from('document_folders').insert({ firm_id: firmId, ...payload }).select().single();
  }
  return { data: null, error };
};

export const updateFolder = async (id, payload) => {
  const { data, error } = await supabase.rpc('update_my_document_folder', {
    p_folder_id: id,
    p_name:      payload?.name  ?? null,
    p_color:     payload?.color ?? null,
  });
  if (!error) return { data, error: null };
  if (error.code === 'PGRST202') {
    return from('document_folders').update(payload).eq('id', id).select().single();
  }
  return { data: null, error };
};

export const deleteFolder = async (id) => {
  const { error } = await supabase.rpc('delete_my_document_folder', { p_folder_id: id });
  if (!error) return { data: true, error: null };
  if (error.code === 'PGRST202') {
    return from('document_folders').delete().eq('id', id);
  }
  return { data: null, error };
};

// ---------------- documents -----------------
//
// `documents` has THREE foreign keys to public.users: `uploaded_by`
// (migration 001), plus `legal_hold_set_by` and `reviewed_by` (added
// in migration 066). When you write `uploaded_by:users(...)` PostgREST
// can't disambiguate which FK to use and raises:
//
//   "Could not embed because more than one relationship was found
//    for 'documents' and 'users'"
//
// The fix is the explicit column-disambiguator syntax: `users!uploaded_by`
// tells PostgREST "embed via the FK on the local `uploaded_by`
// column". This works whether there's one FK or three.
const DOC_SELECT = '*, matter:matters(id,title,matter_number), folder:document_folders(id,name,color), uploaded_by:users!uploaded_by(id,first_name,last_name)';

export const listDocuments = (firmId, opts = {}) => {
  let q = from('documents')
    .select(DOC_SELECT)
    .eq('firm_id', firmId)
    .order('created_at', { ascending: false });
  if (opts.matterId)  q = q.eq('matter_id', opts.matterId);
  if (opts.folderId)  q = q.eq('folder_id', opts.folderId);
  if (opts.category)  q = q.eq('category', opts.category);
  if (opts.limit)     q = q.limit(opts.limit);
  return withTimeout(q, QUERY_TIMEOUT);
};

export const createDocument = (firmId, payload) =>
  from('documents').insert({ firm_id: firmId, ...payload }).select(DOC_SELECT).single();

export const updateDocument = (id, payload) =>
  from('documents').update(payload).eq('id', id).select(DOC_SELECT).single();

export const deleteDocument = (id) => from('documents').delete().eq('id', id);

export const uploadDocumentFile = async (firmId, file) => {
  const ext = file.name.split('.').pop();
  const safeName = `${Date.now()}-${crypto.randomUUID().slice(0, 8)}.${ext}`;
  const path = `${firmId}/${safeName}`;

  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('Upload timed out. Check that the "documents" storage bucket exists in your Supabase dashboard.')), 30000)
  );

  try {
    const { data, error } = await Promise.race([
      supabase.storage.from('documents').upload(path, file, { cacheControl: '3600', upsert: false }),
      timeout,
    ]);
    return { data, error, path };
  } catch (err) {
    return { data: null, error: err, path };
  }
};

export const getDocumentUrl = (path) =>
  supabase.storage.from('documents').createSignedUrl(path, 60 * 60); // 1-hour signed URL

export const deleteDocumentFile = (path) =>
  supabase.storage.from('documents').remove([path]);

// ---------------- document legal hold -----------------
export const setLegalHold = (id, { reason = '', userId = null } = {}) =>
  from('documents').update({
    is_on_legal_hold:  true,
    legal_hold_reason: reason || null,
    legal_hold_set_by: userId  || null,
    legal_hold_set_at: new Date().toISOString(),
  }).eq('id', id).select().single();

export const releaseHold = (id) =>
  from('documents').update({
    is_on_legal_hold:  false,
    legal_hold_reason: null,
    legal_hold_set_by: null,
    legal_hold_set_at: null,
  }).eq('id', id).select().single();

// ---------------- document versions -----------------
export const listDocumentVersions = (documentId) =>
  withTimeout(
    from('document_versions')
      // Same FK-disambiguation pattern as documents (see DOC_SELECT
      // above). If document_versions only ever has one user FK this
      // is harmless; if it gains another later it stays correct.
      .select('*, uploaded_by:users!uploaded_by(id,first_name,last_name)')
      .eq('document_id', documentId)
      .order('version_number', { ascending: false }),
    QUERY_TIMEOUT
  );

export const createDocumentVersion = (payload) =>
  from('document_versions').insert(payload).select().single();

// ---------------- document access log -----------------
export const logDocumentAccess = (documentId, userId, action, metadata = {}) =>
  from('document_access_log').insert({
    document_id: documentId,
    user_id:     userId || null,
    action,
    metadata,
  });

export const listDocumentAccessLog = (documentId, limit = 20) =>
  withTimeout(
    from('document_access_log')
      .select('*, user:users(id,first_name,last_name)')
      .eq('document_id', documentId)
      .order('accessed_at', { ascending: false })
      .limit(limit),
    QUERY_TIMEOUT
  );

// ---------------- employees -----------------
export const listEmployees = async (firmId, opts = {}) => {
  // Preferred: SECURITY DEFINER RPC (migration 120) that resolves firm_id
  // server-side from auth.uid(). Eliminates the failure mode where
  // current_firm_id() in RLS has drifted from public.users.firm_id and
  // employees appear missing in the UI even though they exist in the DB.
  const rpcRes = await supabase.rpc('list_firm_employees');
  if (!rpcRes.error) {
    let rows = Array.isArray(rpcRes.data) ? rpcRes.data : [];
    if (opts.status) rows = rows.filter((r) => r.status === opts.status);
    if (opts.role)   rows = rows.filter((r) => r.role   === opts.role);
    return { data: rows, error: null };
  }
  // Older DB without migration 120 — fall back to the direct RLS-bound query.
  const looksMissing = /PGRST202|Could not find the function|function .*list_firm_employees/i.test(
    rpcRes.error.message || '',
  );
  if (!looksMissing) return { data: null, error: rpcRes.error };

  let q = from('employees').select('*').eq('firm_id', firmId).order('last_name', { ascending: true });
  if (opts.status) q = q.eq('status', opts.status);
  if (opts.role)   q = q.eq('role', opts.role);
  return withTimeout(q, QUERY_TIMEOUT);
};

// Read one employee row, with a server-side fallback for the common
// case where current_firm_id() has drifted from public.users.firm_id and
// RLS hides the row from the direct query. Returns the standard
// PostgREST shape { data, error } so callers don't need to change.
export const fetchEmployee = async (id) => {
  const direct = await from('employees').select('*').eq('id', id).maybeSingle();
  if (direct.data) return direct;
  // Direct path returned nothing (either truly missing OR hidden by RLS
  // drift). Try the SECURITY DEFINER RPC (migration 128) which resolves
  // firm_id from auth.uid() server-side.
  try {
    const { data, error } = await supabase.rpc('get_employee_for_firm', { p_id: id });
    if (error) {
      // If the RPC isn't deployed yet (PGRST202) just surface the direct
      // result so we don't break older environments.
      if (error.code === 'PGRST202') return direct;
      return { data: null, error };
    }
    return { data: data || null, error: null };
  } catch (err) {
    return { data: null, error: err };
  }
};

// Look up an employee by their Supabase auth user UUID (employees.user_id).
// Use this to find the logged-in user's own employee record.
export const fetchEmployeeByUserId = (userId) =>
  from('employees').select('*').eq('user_id', userId).maybeSingle();

// Fallback: look up by the generated EMP string stored in users.employee_id
// (used when employees.user_id hasn't been stamped yet for older records).
export const fetchEmployeeByEmpIdString = (empId) =>
  from('employees').select('*').eq('employee_id', empId).maybeSingle();

// Last-resort fallback: look up by email address.
// Works even when user_id and employee_id haven't been linked on the employees row.
export const fetchEmployeeByEmail = (email) =>
  from('employees').select('*').eq('email', email).maybeSingle();

export const createEmployee = (firmId, payload) =>
  from('employees').insert({ firm_id: firmId, ...payload }).select().single();

export const updateEmployee = (id, payload) =>
  from('employees').update(payload).eq('id', id).select().single();

export const deleteEmployee = (id) => from('employees').delete().eq('id', id);

// Upload a profile photo for an employee.
// Returns { url, error }
export const uploadEmployeePhoto = async (firmId, employeeId, file) => {
  const ext  = file.name.split('.').pop() || 'jpg';
  const path = `${firmId}/${employeeId}-${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('employee-photos')
    .upload(path, file, { upsert: true, contentType: file.type });
  if (error) return { url: null, error };
  const { data } = supabase.storage.from('employee-photos').getPublicUrl(path);
  return { url: data.publicUrl, error: null };
};

// Upload a resume/CV PDF for an employee.
// Returns a signed URL valid for 1 hour so the file stays private.
export const uploadEmployeeResume = async (firmId, employeeId, file) => {
  const ext  = file.name.split('.').pop() || 'pdf';
  const path = `${firmId}/${employeeId}-${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('employee-resumes')
    .upload(path, file, { upsert: true, contentType: file.type });
  if (error) return { url: null, path: null, error };
  // Return the storage path so we can generate fresh signed URLs later
  return { url: null, path, error: null };
};

// Generate a short-lived signed URL for a resume that was previously stored.
export const getEmployeeResumeUrl = async (path) => {
  const { data, error } = await supabase.storage
    .from('employee-resumes')
    .createSignedUrl(path, 60 * 60); // 1-hour
  return { url: data?.signedUrl ?? null, error };
};

// Persist all self-service profile fields in one update.
//
// Tries the SECURITY DEFINER RPC first (migration 128) so the update
// can't silently no-op under current_firm_id() drift. Falls back to the
// direct query for older deployments where the RPC isn't yet present.
export const updateEmployeeProfile = async (id, payload) => {
  try {
    const { data, error } = await supabase.rpc('update_my_employee_profile', {
      p_employee_id: id,
      p_payload:     payload || {},
    });
    if (error) {
      if (error.code === 'PGRST202') {
        // RPC missing — fall through to direct query.
      } else {
        return { data: null, error };
      }
    } else {
      return { data, error: null };
    }
  } catch (err) {
    // Network error etc. — try direct query as a last resort.
    console.warn('updateEmployeeProfile RPC threw; falling back:', err);
  }
  return from('employees').update(payload).eq('id', id).select().single();
};

// ---------------- Ghana card upload -----------------
// entityType: 'contacts' | 'employees'
// Returns { url, error }
export const uploadGhanaCard = async (firmId, entityType, entityId, file) => {
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `${firmId}/${entityType}/${entityId}-${Date.now()}.${ext}`;
  const { error } = await supabase.storage
    .from('ghana-cards')
    .upload(path, file, { upsert: true, contentType: file.type });
  if (error) return { url: null, error };
  const { data } = supabase.storage.from('ghana-cards').getPublicUrl(path);
  return { url: data.publicUrl, error: null };
};

// ---------------- matter users (attorneys / staff assigned to a matter) -----------------
export const listMatterUsers = (matterId) =>
  withTimeout(
    from('matter_users')
      .select('*, user:users(id, first_name, last_name, email, role)')
      .eq('matter_id', matterId)
      .order('role_on_matter', { ascending: false }) // lead_attorney sorts before team_member
      .order('added_at',       { ascending: true }),
    QUERY_TIMEOUT
  );

export const addMatterUser = (firmId, matterId, userId, roleOnMatter = 'team_member') =>
  from('matter_users')
    .upsert(
      { firm_id: firmId, matter_id: matterId, user_id: userId, role_on_matter: roleOnMatter },
      { onConflict: 'matter_id,user_id' }
    )
    .select('*, user:users(id, first_name, last_name, email, role)')
    .single();

export const removeMatterUser = (id) =>
  from('matter_users').delete().eq('id', id);

// ---------------- matter team -----------------
export const listMatterTeam = (matterId) =>
  withTimeout(
    from('matter_team')
      .select('*, employee:employees(id,first_name,last_name,role,title,phone,email)')
      .eq('matter_id', matterId)
      .order('added_at', { ascending: true }),
    QUERY_TIMEOUT
  );

export const addMatterTeamMember = (firmId, matterId, employeeId, roleOnMatter) =>
  from('matter_team').insert({ firm_id: firmId, matter_id: matterId, employee_id: employeeId, role_on_matter: roleOnMatter }).select().single();

export const removeMatterTeamMember = (id) => from('matter_team').delete().eq('id', id);

// Fetch all matters an employee is assigned to (via matter_team)
export const listEmployeeMatters = (employeeId) =>
  withTimeout(
    from('matter_team')
      .select('role_on_matter, matter:matters(id,title,matter_number,status,practice_area,created_at)')
      .eq('employee_id', employeeId)
      .order('added_at', { ascending: false }),
    QUERY_TIMEOUT
  );

// ---------------- activity -----------------
// logActivity is intentionally fire-and-forget.
// We use an async IIFE so the Supabase insert promise is actually consumed
// (Supabase JS v2 builders are lazy — they only fire when awaited/.then'd).
// Errors are swallowed silently so logging never breaks the calling action.
export const logActivity = (firmId, entry) => {
  (async () => {
    try {
      await supabase
        .from('activity_log')
        .insert({ firm_id: firmId, ...entry });
    } catch (_) { /* fire-and-forget */ }
  })();
};

export const listActivity = (firmId, limit = 25) =>
  withTimeout(
    from('activity_log')
      .select('*, user:users(id, first_name, last_name)')
      .eq('firm_id', firmId)
      .order('created_at', { ascending: false })
      .limit(limit),
    QUERY_TIMEOUT
  );

// ---------------- client portal -----------------
// Fetch the contact record for the signed-in portal user.
// Uses get_portal_contact_for_current_user() which:
//   1. Matches by portal_user_id (fast path for email-invite users)
//   2. Falls back to email match (magic-link / OTP users)
//   3. Auto-links portal_user_id on first magic-link login
export const fetchPortalContact = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: null };

  // Try the smart RPC first (migration 047) — matches by portal_user_id OR email,
  // and auto-links portal_user_id on first magic-link login.
  const { data: rpcData, error: rpcErr } = await supabase.rpc('get_portal_contact_for_current_user');
  if (!rpcErr) {
    const row = Array.isArray(rpcData) ? (rpcData[0] ?? null) : (rpcData ?? null);
    return { data: row, error: null };
  }

  // Fallback (pre-047): match only by portal_user_id
  return from('contacts')
    .select('*')
    .eq('portal_user_id', user.id)
    .maybeSingle();
};

// Portal: list matters where this contact is the client (RLS enforces it).
export const listPortalMatters = () =>
  from('matters')
    .select('*')
    .order('created_at', { ascending: false });

// Portal: matter detail.
export const fetchPortalMatter = (matterId) =>
  from('matters').select('*').eq('id', matterId).maybeSingle();

// Portal: invoices for the client's matters (RLS restricts automatically).
// Portal: only show invoices the client should see — never drafts or void.
export const listPortalInvoices = () =>
  from('invoices')
    .select('*, matter:matters(id,title,matter_number,flat_fee_amount,fee_arrangement,agreed_fee)')
    .not('status', 'in', '(draft,void)')
    .order('created_at', { ascending: false });

// Portal: a single invoice with line items for download/print.
// Returns { data, error } where data has line_items.
export const getPortalInvoice = (invoiceId) =>
  from('invoices')
    .select('*, matter:matters(id,title,matter_number), line_items:invoice_line_items(*)')
    .eq('id', invoiceId)
    .maybeSingle();

// Portal: payment receipts for the signed-in client.
export const listPortalReceipts = () =>
  from('payments')
    .select('*, matter:matters(id,title,matter_number)')
    .not('receipt_number', 'is', null)
    .order('payment_date', { ascending: false });

// Portal: full billing picture for a matter — payments + charges + agreed fee.
// Used by portal dashboard and matter detail to show accurate balance.
export const fetchPortalMatterBilling = async (matterId) => {
  const [paymentsRes, chargesRes] = await Promise.all([
    from('payments')
      .select('id,amount,payment_date,method,payment_label,receipt_number')
      .eq('matter_id', matterId)
      .order('payment_date', { ascending: false }),
    from('case_charges')
      .select('id,description,amount,charge_date,charge_type,status')
      .eq('matter_id', matterId)
      .order('charge_date', { ascending: false }),
  ]);
  return {
    payments: paymentsRes.data || [],
    charges:  chargesRes.data  || [],
    error:    paymentsRes.error || chargesRes.error || null,
  };
};

// Portal: all payments for the client's matters (for dashboard totals).
export const listPortalMatterPayments = () =>
  from('payments')
    .select('id,matter_id,amount,payment_date,method,payment_label,receipt_number')
    .order('payment_date', { ascending: false });

// Portal: all charges for the client's matters.
export const listPortalMatterCharges = () =>
  from('case_charges')
    .select('id,matter_id,description,amount,charge_date,charge_type,status')
    .order('charge_date', { ascending: false });

// Portal: documents shared with client for a specific matter.
export const listPortalDocumentsForMatter = (matterId) =>
  from('documents')
    .select('id, title, file_path, mime_type, file_size, created_at, category, classification')
    .eq('matter_id', matterId)
    .eq('portal_visible', true)
    .order('created_at', { ascending: false });

// Portal messages – both client and firm use these helpers.
export const listPortalMessages = (contactId) =>
  from('portal_messages')
    .select('*')
    .eq('contact_id', contactId)
    .order('created_at', { ascending: true });

// sendPortalMessage — firm side. Routes through a SECURITY DEFINER RPC
// (migration 123) that resolves firm_id server-side from auth.uid() and
// evaluates the private-thread + firm-leader + participant checks with
// authoritative server state. Eliminates the 403 RLS denials that hit
// clients whose current_firm_id() had drifted.
//
// Falls back to the original direct-insert path for portal clients
// (where senderType='client') and for DBs without migration 123.
export const sendPortalMessage = async (firmId, contactId, senderId, senderType, bodyOrOpts) => {
  const opts = typeof bodyOrOpts === 'string'
    ? { body: bodyOrOpts, attachments: [] }
    : { body: bodyOrOpts?.body ?? null, attachments: bodyOrOpts?.attachments || [], id: bodyOrOpts?.id };

  // Firm-side send uses the RPC for reliability.
  if (senderType === 'firm') {
    const rpc = await supabase.rpc('send_portal_message', {
      p_contact_id:  contactId,
      p_body:        opts.body || null,
      p_attachments: opts.attachments || [],
      p_client_id:   opts.id || null,
    });
    if (!rpc.error) {
      const row = Array.isArray(rpc.data) ? rpc.data[0] : rpc.data;
      return { data: row, error: null };
    }
    const looksMissing = /PGRST202|Could not find the function|function .*send_portal_message/i.test(
      rpc.error.message || '',
    );
    if (!looksMissing) return { data: null, error: rpc.error };
    // else: fall through to direct insert on older DBs
  }

  const insert = {
    firm_id:     firmId,
    contact_id:  contactId,
    sender_id:   senderId,
    sender_type: senderType,
    body:        opts.body || null,
    attachments: opts.attachments,
  };
  if (opts.id) insert.id = opts.id;
  return from('portal_messages').insert(insert).select().single();
};

export const markPortalMessagesRead = (contactId) =>
  from('portal_messages')
    .update({ read_at: new Date().toISOString() })
    .eq('contact_id', contactId)
    .is('read_at', null)
    .neq('sender_type', 'client');

// Invite a client to the portal via the Edge Function.
export const invitePortalClient = async (contactId) => {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch(
    `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/invite-portal-client`,
    {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${session?.access_token}`,
      },
      body: JSON.stringify({
        contact_id:  contactId,
        redirect_to: `${window.location.origin}/#/portal`,
      }),
    },
  );
  const json = await res.json();
  if (!res.ok) return { data: null, error: { message: json.error || 'Invite failed' } };
  return { data: json, error: null };
};

// Firm-side: list portal messages for a given contact. Uses SECURITY
// DEFINER RPC (migration 125) to bypass RLS drift; falls back to direct
// query on older DBs.
export const listFirmPortalMessages = async (contactId) => {
  const rpc = await supabase.rpc('list_portal_messages_for_contact', { p_contact_id: contactId });
  if (!rpc.error) return { data: Array.isArray(rpc.data) ? rpc.data : [], error: null };
  const looksMissing = /PGRST202|Could not find the function|function .*list_portal_messages_for_contact/i.test(
    rpc.error.message || '',
  );
  if (!looksMissing) return { data: null, error: rpc.error };
  return from('portal_messages')
    .select('*')
    .eq('contact_id', contactId)
    .order('created_at', { ascending: true });
};

export const countUnreadPortalMessages = (firmId) =>
  from('portal_messages')
    .select('id', { count: 'exact', head: true })
    .eq('firm_id', firmId)
    .eq('sender_type', 'client')
    .is('read_at', null);

// Firm-side: get all contacts that have at least one message, with last
// message preview and unread count — used to build the conversation list.
export const listFirmConversations = async (firmId) => {
  // Preferred: SECURITY DEFINER RPC (migration 123) that resolves firm_id
  // server-side. Mirrors the same pattern used by add_staff_record /
  // list_firm_employees — eliminates the "I sent a message but I don't see
  // it after refresh" failure mode caused by current_firm_id() drift.
  const rpc = await supabase.rpc('list_firm_conversations');
  if (!rpc.error) {
    return { data: Array.isArray(rpc.data) ? rpc.data : [], error: null };
  }
  const looksMissing = /PGRST202|Could not find the function|function .*list_firm_conversations/i.test(
    rpc.error.message || '',
  );
  if (!looksMissing) return { data: null, error: rpc.error };

  // ── Legacy direct-query fallback (older DBs without migration 123) ──
  const { data, error } = await from('portal_messages')
    .select('id, contact_id, sender_type, body, created_at, read_at')
    .eq('firm_id', firmId)
    .order('created_at', { ascending: false })
    .limit(500);

  if (error) return { data: null, error };

  const contactIds = [...new Set((data || []).map((m) => m.contact_id))];
  if (!contactIds.length) return { data: [], error: null };

  const { data: contacts, error: cErr } = await from('contacts')
    .select('id, first_name, last_name, email, organization, contact_type, portal_thread_private')
    .in('id', contactIds);

  if (cErr) return { data: null, error: cErr };

  const contactMap = Object.fromEntries((contacts || []).map((c) => [c.id, c]));

  const convMap = new Map();
  for (const msg of data || []) {
    if (!convMap.has(msg.contact_id)) {
      convMap.set(msg.contact_id, {
        contact:     contactMap[msg.contact_id] || { id: msg.contact_id },
        lastMessage: msg,
        unreadCount: 0,
      });
    }
    if (msg.sender_type === 'client' && !msg.read_at) {
      convMap.get(msg.contact_id).unreadCount++;
    }
  }

  return { data: Array.from(convMap.values()), error: null };
};

// Firm-side: mark client messages in a thread as read. RPC bypasses
// the RLS UPDATE policy on portal_messages; falls back to direct update.
export const markFirmMessagesRead = async (contactId) => {
  const rpc = await supabase.rpc('mark_firm_messages_read', { p_contact_id: contactId });
  if (!rpc.error) return { data: rpc.data, error: null };
  const looksMissing = /PGRST202|Could not find the function|function .*mark_firm_messages_read/i.test(
    rpc.error.message || '',
  );
  if (!looksMissing) return { data: null, error: rpc.error };
  return from('portal_messages')
    .update({ read_at: new Date().toISOString() })
    .eq('contact_id', contactId)
    .eq('sender_type', 'client')
    .is('read_at', null);
};

// ─── Internal team chat (firm_channels + firm_messages) ─────────────────────

// Public channels only.
//
// Preferred: SECURITY DEFINER RPC (migration 124) resolves firm_id
// server-side. Eliminates the failure mode where current_firm_id() in
// RLS drifts from public.users.firm_id and returns an empty channel list
// even though the KPI counters (which use a different RPC) show "4 channels".
//
// Defensive fallback: tiered direct-query retry that drops filters tied
// to missing columns (is_private from 051, archived_at from 102).
export const listFirmChannels = async (firmId) => {
  // Tier 0: RPC.
  const rpc = await supabase.rpc('list_firm_channels');
  if (!rpc.error) return { data: Array.isArray(rpc.data) ? rpc.data : [], error: null };
  const looksMissingRpc = /PGRST202|Could not find the function|function .*list_firm_channels/i.test(
    rpc.error.message || '',
  );
  if (!looksMissingRpc) {
    // RPC exists but failed for some other reason — surface it.
    return { data: null, error: rpc.error };
  }

  // Tier 1: full query with both filters.
  let res = await from('firm_channels')
    .select('*')
    .eq('firm_id', firmId)
    .eq('is_private', false)
    .is('archived_at', null)
    .order('name', { ascending: true });
  if (!res.error) return res;

  const msg = res.error?.message || '';
  // PostgREST returns 400 with "column firm_channels.X does not exist" when
  // a referenced column is missing. Strip and retry.
  if (/column .*archived_at.*does not exist/i.test(msg)) {
    // eslint-disable-next-line no-console
    console.warn('[listFirmChannels] archived_at missing — retrying without it. Apply migration 102 to fix.');
    res = await from('firm_channels')
      .select('*')
      .eq('firm_id', firmId)
      .eq('is_private', false)
      .order('name', { ascending: true });
    if (!res.error) return res;
  }
  if (/column .*is_private.*does not exist/i.test(res.error?.message || '')) {
    // eslint-disable-next-line no-console
    console.warn('[listFirmChannels] is_private missing — retrying without it. Apply migration 051 to fix.');
    res = await from('firm_channels')
      .select('*')
      .eq('firm_id', firmId)
      .order('name', { ascending: true });
    if (!res.error) return res;
  }

  // Last resort — the very minimum that works on a migration-048 schema.
  // eslint-disable-next-line no-console
  console.warn('[listFirmChannels] both filters dropped — returning all rows.');
  return from('firm_channels')
    .select('id, firm_id, name, description, created_at, created_by')
    .eq('firm_id', firmId)
    .order('name', { ascending: true });
};

// createFirmChannel — routes through the SECURITY DEFINER RPC
// `create_my_firm_channel` (migration 137) which resolves firm_id from
// auth.uid() server-side. Fixes the "new row violates row-level
// security policy for table firm_channels" error that fired when
// current_firm_id() had drifted.
//
// Falls back to the legacy direct INSERT on older DBs that don't have
// the RPC. firmId and userId remain in the signature for caller compat
// but are NOT forwarded to the RPC — the server is the authoritative
// source for both.
// eslint-disable-next-line no-unused-vars
export const createFirmChannel = async (firmId, name, description, userId) => {
  const slug = String(name || '').toLowerCase().replace(/\s+/g, '-');

  const rpc = await supabase.rpc('create_my_firm_channel', {
    p_name:        name,
    p_description: description ?? null,
  });
  if (!rpc.error) return { data: rpc.data, error: null };

  const looksMissing = /PGRST202|Could not find the function|function .*create_my_firm_channel/i.test(
    rpc.error.message || '',
  );
  if (!looksMissing) return { data: null, error: rpc.error };

  // Legacy fallback: direct INSERT. Will only succeed on DBs where
  // current_firm_id() matches the supplied firmId.
  return from('firm_channels')
    .insert({ firm_id: firmId, name: slug, description, created_by: userId, is_private: false })
    .select()
    .single();
};

// Private channels / DMs — returns channels where the current user is a member.
// Tries the SECURITY DEFINER RPC first (migration 124); falls back to the
// original two-step query for older DBs.
export const listFirmPrivateChannels = async (firmId, userId) => {
  const rpc = await supabase.rpc('list_firm_private_channels');
  if (!rpc.error) return { data: Array.isArray(rpc.data) ? rpc.data : [], error: null };
  const looksMissing = /PGRST202|Could not find the function|function .*list_firm_private_channels/i.test(
    rpc.error.message || '',
  );
  if (!looksMissing) return { data: null, error: rpc.error };

  // Fetch private channels this user belongs to (legacy path).
  const { data: memberships, error: mErr } = await from('firm_channel_members')
    .select('channel_id')
    .eq('user_id', userId);
  if (mErr || !memberships?.length) return { data: [], error: mErr };

  const channelIds = memberships.map((m) => m.channel_id);

  const { data: channels, error: cErr } = await from('firm_channels')
    .select('*')
    .eq('firm_id', firmId)
    .eq('is_private', true)
    .is('archived_at', null)
    .in('id', channelIds)
    .order('created_at', { ascending: false });
  if (cErr) return { data: null, error: cErr };

  // For each channel attach its members (with user details) for display
  const { data: allMembers, error: allErr } = await from('firm_channel_members')
    .select('channel_id, user_id, user:users(id, first_name, last_name, email)')
    .in('channel_id', channelIds);
  if (allErr) return { data: null, error: allErr };

  const membersByChannel = {};
  for (const m of allMembers || []) {
    if (!membersByChannel[m.channel_id]) membersByChannel[m.channel_id] = [];
    if (m.user) membersByChannel[m.channel_id].push(m.user);
  }

  return {
    data: (channels || []).map((ch) => ({ ...ch, members: membersByChannel[ch.id] || [] })),
    error: null,
  };
};

// Create a private channel (DM or group) via SECURITY DEFINER RPC to bypass RLS.
export const createPrivateChannel = async (firmId, memberIds, name, userId) => {
  // For 1:1 DMs, reuse an existing channel if one already exists
  if (memberIds.length === 2) {
    const { data: existing } = await listFirmPrivateChannels(firmId, userId);
    const dm = (existing || []).find((ch) =>
      ch.members.length === 2 &&
      ch.members.every((m) => memberIds.includes(m.id))
    );
    if (dm) return { data: dm, error: null, existing: true };
  }

  const channelName = name || `dm-${Date.now()}`;
  const { data: channel, error } = await supabase.rpc('create_private_channel', {
    p_firm_id:    firmId,
    p_name:       channelName,
    p_member_ids: memberIds,
  });
  if (error) return { data: null, error };

  return { data: { ...(channel || {}), members: [] }, error: null };
};

export const listChannelMembers = (channelId) =>
  from('firm_channel_members')
    .select('user_id, user:users(id, first_name, last_name, email)')
    .eq('channel_id', channelId);

// Mark a channel as fully read for the current user (upsert last_read_at = now).
export const markChannelRead = (channelId, userId) =>
  supabase
    .from('firm_channel_reads')
    .upsert(
      { user_id: userId, channel_id: channelId, last_read_at: new Date().toISOString() },
      { onConflict: 'user_id,channel_id' }
    );

// Returns rows { channel_id, unread_count } for channels that have unread messages.
export const getChannelUnreadCounts = (userId) =>
  supabase.rpc('get_channel_unread_counts', { p_user_id: userId });

// Add a single member to a private channel via SECURITY DEFINER RPC.
// Any existing channel member can call this; direct inserts are blocked by RLS.
export const addChannelMember = (channelId, newUserId) =>
  supabase.rpc('add_channel_member', {
    p_channel_id:  channelId,
    p_new_user_id: newUserId,
  });

// ─── Portal thread participants (firm users assigned to a client thread) ──────

// List firm users participating in a specific client thread.
export const listThreadParticipants = (contactId) =>
  from('portal_thread_participants')
    .select('user_id, user:users(id, first_name, last_name, email)')
    .eq('contact_id', contactId);

// Add a firm user to a client thread. Goes through the SECURITY DEFINER
// RPC `add_thread_participant` (migration 110) which does explicit
// firm-membership + private-thread-leader checks and bypasses the
// portal_thread_participants RLS — fixes the recurring "new row violates
// RLS" error on this table whose policy history is too tangled to trust.
// firmId is unused now (kept in the signature for caller compatibility).
// eslint-disable-next-line no-unused-vars
export const addThreadParticipant = (firmId, contactId, userId) =>
  supabase.rpc('add_thread_participant', {
    p_contact_id: contactId,
    p_user_id:    userId,
  });

// Remove a firm user from a client thread — same RPC pattern.
export const removeThreadParticipant = (contactId, userId) =>
  supabase.rpc('remove_thread_participant', {
    p_contact_id: contactId,
    p_user_id:    userId,
  });

// Two-step query: fetch messages then attach sender details from public.users.
// Uses SECURITY DEFINER RPC (migration 125) when available — returns
// the messages with sender already joined in one round-trip.
// Falls back to the original two-step query on older DBs.
export const listFirmMessages = async (channelId) => {
  const rpc = await supabase.rpc('list_firm_messages', { p_channel_id: channelId });
  if (!rpc.error) return { data: Array.isArray(rpc.data) ? rpc.data : [], error: null };
  const looksMissing = /PGRST202|Could not find the function|function .*list_firm_messages/i.test(
    rpc.error.message || '',
  );
  if (!looksMissing) return { data: null, error: rpc.error };

  const { data: messages, error } = await from('firm_messages')
    .select('*')
    .eq('channel_id', channelId)
    .is('scheduled_for', null)
    .order('created_at', { ascending: true })
    .limit(200);

  if (error || !messages?.length) return { data: messages || [], error };

  const senderIds = [...new Set(messages.map((m) => m.sender_id).filter(Boolean))];
  const { data: users } = await from('users')
    .select('id, first_name, last_name, email')
    .in('id', senderIds);

  const userMap = Object.fromEntries((users || []).map((u) => [u.id, u]));
  return {
    data: messages.map((m) => ({ ...m, sender: userMap[m.sender_id] || null })),
    error: null,
  };
};

// sendFirmMessage accepts either a string body (legacy callers) or an options
// object `{ id, body, attachments, mentions, parent_message_id, scheduled_for }`.
// Routes through a SECURITY DEFINER RPC (migration 125) that resolves
// firm_id server-side from auth.uid() — eliminates the 403 RLS denial
// failure mode that bit users when current_firm_id() drifted.
export const sendFirmMessage = async (firmId, channelId, senderId, bodyOrOpts) => {
  const opts = typeof bodyOrOpts === 'string' ? { body: bodyOrOpts } : (bodyOrOpts || {});

  const rpc = await supabase.rpc('send_firm_message', {
    p_channel_id:        channelId,
    p_body:              opts.body ?? null,
    p_attachments:       opts.attachments || [],
    p_mentions:          opts.mentions    || [],
    p_client_id:         opts.id || null,
    p_parent_message_id: opts.parent_message_id || null,
    p_scheduled_for:     opts.scheduled_for     || null,
  });
  if (!rpc.error) {
    const row = Array.isArray(rpc.data) ? rpc.data[0] : rpc.data;
    if (row) {
      // Attach sender details so the caller can render the message immediately.
      const { data: sender } = await from('users')
        .select('id, first_name, last_name, email')
        .eq('id', senderId)
        .maybeSingle();
      return { data: { ...row, sender: sender || null }, error: null };
    }
  }
  const looksMissing = /PGRST202|Could not find the function|function .*send_firm_message/i.test(
    rpc.error?.message || '',
  );
  if (rpc.error && !looksMissing) return { data: null, error: rpc.error };

  // ── Legacy direct insert ─────────────────────────────────────────────
  const insert = {
    firm_id:     firmId,
    channel_id:  channelId,
    sender_id:   senderId,
    body:        opts.body ?? null,
    attachments: opts.attachments || [],
    mentions:    opts.mentions    || [],
  };
  if (opts.id)                insert.id = opts.id;
  if (opts.parent_message_id) insert.parent_message_id = opts.parent_message_id;
  if (opts.scheduled_for)     insert.scheduled_for     = opts.scheduled_for;

  const { data: msg, error } = await from('firm_messages')
    .insert(insert)
    .select('*')
    .single();

  if (error || !msg) return { data: msg, error };

  const { data: sender } = await from('users')
    .select('id, first_name, last_name, email')
    .eq('id', senderId)
    .maybeSingle();

  return { data: { ...msg, sender: sender || null }, error: null };
};

export const updateFirmMessage = (messageId, body) =>
  from('firm_messages')
    .update({ body, edited_at: new Date().toISOString() })
    .eq('id', messageId)
    .select('*')
    .single();

// ─── Messaging enhancements (migration 098) ──────────────────────────────

const ATTACH_BUCKET = 'message-attachments';

// Sanitise a filename so it's safe in a storage path. Keep an extension if
// present, replace anything that isn't alphanum/dot/dash/underscore.
function safeName(name) {
  return String(name || 'file')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'file';
}

// scope='firm' → path: firm/<firmId>/<channelId>/<messageId>/<filename>
// scope='portal' → path: portal/<firmId>/<contactId>/<messageId>/<filename>
function attachmentPath({ scope, firmId, channelId, contactId, messageId, file }) {
  const stamp = Date.now();
  const fn    = `${stamp}-${safeName(file.name)}`;
  return scope === 'firm'
    ? `firm/${firmId}/${channelId}/${messageId}/${fn}`
    : `portal/${firmId}/${contactId}/${messageId}/${fn}`;
}

// Upload one file to the message-attachments bucket and return an attachment
// descriptor suitable for insertion into `firm_messages.attachments` /
// `portal_messages.attachments`. The caller pre-generates `messageId` so the
// upload lands at the same path the message row will reference.
export async function uploadMessageAttachment(scope, ids, file) {
  const path = attachmentPath({
    scope,
    firmId:     ids.firmId,
    channelId:  ids.channelId,
    contactId:  ids.contactId,
    messageId:  ids.messageId,
    file,
  });
  const { error } = await supabase.storage.from(ATTACH_BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) return { data: null, error };
  return {
    data: {
      path,
      name: file.name,
      size: file.size,
      mime: file.type || 'application/octet-stream',
    },
    error: null,
  };
}

// Short-lived signed URL for displaying / downloading an attachment.
// 60 minutes is enough for the user to scroll back without re-fetching too
// often, and short enough that leaked URLs aren't a long-term liability.
export const signedAttachmentUrl = (path, expiresIn = 3600) =>
  supabase.storage.from(ATTACH_BUCKET).createSignedUrl(path, expiresIn);

// Batch version so chat panes can resolve many URLs in one round trip.
export const signedAttachmentUrls = (paths, expiresIn = 3600) =>
  supabase.storage.from(ATTACH_BUCKET).createSignedUrls(paths, expiresIn);

// Soft-delete via SECURITY DEFINER RPCs — keeps the row + the tombstone in
// the thread so realtime subscribers can re-render the deletion in place.
export const softDeleteFirmMessage   = (messageId) =>
  supabase.rpc('soft_delete_firm_message',   { p_message_id: messageId });
export const softDeletePortalMessage = (messageId) =>
  supabase.rpc('soft_delete_portal_message', { p_message_id: messageId });

// ─── Reactions ────────────────────────────────────────────────────────────
// Used by both firm and portal scopes — the `scope` column on
// public.message_reactions discriminates.

export const listMessageReactions = (scope, messageIds) =>
  from('message_reactions')
    .select('message_id, emoji, user_id, user:users(id, first_name, last_name, email)')
    .eq('scope', scope)
    .in('message_id', messageIds);

export const addReaction = (scope, messageId, firmId, userId, emoji) =>
  from('message_reactions').insert({
    scope,
    message_id: messageId,
    firm_id:    firmId,
    user_id:    userId,
    emoji,
  });

export const removeReaction = (scope, messageId, userId, emoji) =>
  from('message_reactions')
    .delete()
    .eq('scope', scope)
    .eq('message_id', messageId)
    .eq('user_id', userId)
    .eq('emoji', emoji);

// Toggle convenience: if the user already reacted with this emoji, remove
// it; otherwise add it. Insert errors with code 23505 (unique violation)
// mean the row already exists, so we fall through to delete.
export const toggleReaction = async (scope, messageId, firmId, userId, emoji) => {
  const { error } = await addReaction(scope, messageId, firmId, userId, emoji);
  if (!error) return { data: { action: 'added' }, error: null };
  if (error.code === '23505') {
    const { error: delErr } = await removeReaction(scope, messageId, userId, emoji);
    if (delErr) return { data: null, error: delErr };
    return { data: { action: 'removed' }, error: null };
  }
  return { data: null, error };
};

// ─── @mention search ─────────────────────────────────────────────────────
// Returns up to 10 firm members matching the query. Server-side trimmed +
// scoped to the caller's firm (see migration 098).
export const searchFirmMembers = (query) =>
  supabase.rpc('search_firm_members', { p_query: query ?? null });

// ─── Portal-thread privacy (migration 099) ────────────────────────────────
// Soft restriction: flipping a thread private hides it from firm members who
// aren't on the participant list (or aren't Admin/Owner/Partner).
export const setPortalThreadPrivacy = (contactId, isPrivate) =>
  supabase.rpc('set_portal_thread_privacy', {
    p_contact_id: contactId,
    p_private:    !!isPrivate,
  });

// Single-query messaging overview for the page header strip (migration 100).
export const getMessagingOverview = () => supabase.rpc('get_messaging_overview');

// ─── Scheduled messages, bookmarks, export (migration 104) ──────────────
// scheduled_for is a timestamptz; pass an ISO string from the composer.

// Caller's own scheduled-but-not-yet-sent messages — used by a "scheduled"
// drawer in the composer so users can edit / cancel before send.
export const listMyScheduledMessages = (firmId, userId) =>
  from('firm_messages')
    .select('id, channel_id, body, attachments, scheduled_for, channel:firm_channels(name, is_private)')
    .eq('firm_id', firmId)
    .eq('sender_id', userId)
    .not('scheduled_for', 'is', null)
    .gte('scheduled_for', new Date().toISOString())
    .order('scheduled_for', { ascending: true });

// Cancel a scheduled message — sender only. RLS on firm_messages restricts
// updates to firm members, and a check below ensures the sender_id matches.
export const cancelScheduledMessage = (messageId) =>
  from('firm_messages')
    .delete()
    .eq('id', messageId)
    .not('scheduled_for', 'is', null);

// Trigger the cron-style processor manually (super-admin only by default).
export const processScheduledMessages = () =>
  supabase.functions.invoke('process-scheduled-messages', { body: {} });

// Bookmarks
export const bookmarkFirmMessage   = (messageId, note = null) =>
  from('firm_message_bookmarks').insert({ message_id: messageId, user_id: undefined, note }).select().single();
// The user_id is filled by the RLS WITH CHECK using auth.uid(); we still
// set it explicitly for resilience to non-PostgREST clients.
export const bookmarkMessage = async (messageId, note = null) => {
  const { data: u } = await supabase.auth.getUser();
  const uid = u?.user?.id;
  if (!uid) return { data: null, error: { message: 'Not authenticated' } };
  return from('firm_message_bookmarks').insert({ message_id: messageId, user_id: uid, note }).select().single();
};
export const removeBookmark = async (messageId) => {
  const { data: u } = await supabase.auth.getUser();
  const uid = u?.user?.id;
  if (!uid) return { data: null, error: { message: 'Not authenticated' } };
  return from('firm_message_bookmarks').delete().eq('message_id', messageId).eq('user_id', uid);
};

export const listMyBookmarks = () => supabase.rpc('list_my_bookmarks');

// Firm-wide export — leader-gated, returns a deep JSON tree.
export const firmMessagingExport = () => supabase.rpc('firm_messaging_export');

// ─── Message retention (migration 105) ──────────────────────────────────
// Days are nullable — null clears the policy (no auto-deletion).
export const setFirmMessageRetention = (days) =>
  supabase.rpc('set_firm_message_retention', { p_days: days });

// Preview the impact of a retention policy before turning it on or changing
// the window. `proposedDays = null` previews the current effective policy.
export const previewMessageRetention = (proposedDays = null) =>
  supabase.rpc('preview_message_retention', { p_proposed_days: proposedDays });

// Apply retention manually (super-admin only). Cron normally calls the
// apply-message-retention edge function with x-cron-secret instead.
export const applyMessageRetention = (dryRun = true) =>
  supabase.functions.invoke('apply-message-retention', { body: { dry_run: dryRun } });

// ─── Tamper-evident integrity (migration 106) ───────────────────────────
export const verifyChannelIntegrity = (channelId) =>
  supabase.rpc('verify_channel_integrity', { p_channel_id: channelId });

// Walks every channel in the caller's firm and returns a per-channel
// integrity report — used by the Settings "Integrity report" button.
export const verifyFirmIntegrity   = () => supabase.rpc('verify_firm_integrity');

// Manual snapshot — typically a daily cron, but firm leaders can trigger
// a snapshot to anchor the current state (e.g. before they share the chat
// archive with opposing counsel during discovery).
export const recordMessagingSeals  = () => supabase.rpc('record_messaging_seals');

// Latest seal per channel — useful to show "Last sealed: X days ago" in
// the integrity report so leaders know how fresh the anchor is.
export const listLatestSeals = (firmId) =>
  from('messaging_seals')
    .select('channel_id, captured_at, head_chain_hash, message_count')
    .eq('firm_id', firmId)
    .order('captured_at', { ascending: false })
    .limit(500);

// ─── OpenTimestamps anchors (migration 109) ────────────────────────────
// Firm-leader read of the anchor ledger. The `ots_proof` blob is fetched
// lazily on download so the list query stays cheap.
export const listSealAnchors = (firmId) =>
  from('messaging_seal_anchors')
    .select('id, covers_since, covers_until, root_hash, seal_count, calendars, status, error_text, created_at')
    .eq('firm_id', firmId)
    .order('created_at', { ascending: false })
    .limit(100);

// Fetch the .ots proof bytes for a specific anchor. Supabase returns bytea
// as a hex string prefixed by '\x'; caller decodes and offers as download.
export const fetchSealAnchorProof = (anchorId) =>
  from('messaging_seal_anchors')
    .select('ots_proof')
    .eq('id', anchorId)
    .maybeSingle();

// Manual trigger (firm leader). Cron normally hits the edge function with
// the cron-secret header instead — see deploy notes in the migration.
export const triggerSealAnchor = (opts = {}) =>
  supabase.functions.invoke('anchor-messaging-seals', { body: opts });

// Check each pending anchor against its calendar and flip status to
// 'confirmed' once the Bitcoin attestation arrives. Designed for cron
// (hourly is plenty); also exposed via a manual "Check for upgrades"
// button in Settings.
export const upgradeAnchors = (opts = {}) =>
  supabase.functions.invoke('upgrade-messaging-anchors', { body: opts });

// ─── Channel-level end-to-end encryption toggle (migration 111) ─────────
// Marks a channel as encrypted. Firm leader gated server-side; requires the
// firm to have run setup_firm_encryption first (otherwise the RPC refuses).
export const setChannelEncryption = (channelId, encrypted) =>
  supabase.rpc('set_channel_encryption', { p_channel_id: channelId, p_encrypted: !!encrypted });

// Enqueue the daily seal-digest email to all firm leaders. Firm-leader
// JWT scope sends only their own firm; super-admin scope sends every firm.
// Pass { since } to override the default 25-hour lookback window.
export const sendSealDigest = (opts = {}) =>
  supabase.functions.invoke('send-seal-digest', { body: opts });

// ─── Web Push fan-out (migration 107) ───────────────────────────────────
// Fire-and-forget so it never blocks a message send. The edge function
// returns quickly anyway — push services queue, then deliver. Use this on
// the client side right after sending a message that should generate a
// notification (e.g. @-mentions, portal-client → firm).
export const fireWebPush = (payload) => {
  // Returns a promise but callers typically don't await.
  return supabase.functions.invoke('send-web-push', { body: payload })
    .catch((err) => ({ error: err }));
};

// ─── DLP settings + events (migration 108) ──────────────────────────────
// Per-firm DLP configuration. Row may not exist yet for a freshly created
// firm — callers should treat absence as the migration's default policy.
export const getFirmDlpSettings = (firmId) =>
  from('firm_dlp_settings')
    .select('*')
    .eq('firm_id', firmId)
    .maybeSingle();

export const updateFirmDlpSettings = (settings) =>
  supabase.rpc('update_firm_dlp_settings', { p_settings: settings });

export const listRecentDlpEvents = (firmId, limit = 50) =>
  from('dlp_events')
    .select('id, scope, message_id, rule, severity, redacted_match, created_at, user:users!user_id(id, first_name, last_name, email)')
    .eq('firm_id', firmId)
    .order('created_at', { ascending: false })
    .limit(limit);

// ─── Message search (migration 101) ─────────────────────────────────────
export const searchMessages = (query, limit = 30) =>
  supabase.rpc('search_messages', { p_query: query, p_limit: limit });

// ─── Threads / pins / archive / edit history (migration 102) ────────────
export const editFirmMessageRpc       = (messageId, body) => supabase.rpc('edit_firm_message',         { p_message_id: messageId, p_body: body });
export const togglePinFirmMessage     = (messageId)       => supabase.rpc('toggle_pin_firm_message',   { p_message_id: messageId });
export const archiveFirmChannel       = (channelId, archive = true) =>
  supabase.rpc('archive_firm_channel', { p_channel_id: channelId, p_archive: archive });
export const setChannelMatter         = (channelId, matterId)   => supabase.rpc('set_channel_matter', { p_channel_id: channelId, p_matter_id: matterId });

// Channels linked to a specific matter — used by the matter detail page's
// "Team channels" panel. Ignores archived channels by default.
export const listChannelsForMatter = (matterId) =>
  from('firm_channels')
    .select('id, name, description, is_private, matter_id, archived_at, created_at')
    .eq('matter_id', matterId)
    .is('archived_at', null)
    .order('name', { ascending: true });

// Archived channels for the admin "trash" view in Settings → Team.
// Lists channels archived by firm leaders, displayed on Settings →
// Team & roles. We can't use PostgREST's embedded select for the
// `archiver` user because firm_channels.archived_by references auth.users
// (not public.users), and PostgREST doesn't expose auth schema by
// default. Two-step fetch: channels first, then look up archiver names
// from public.users (matching on id) and merge client-side.
export const listArchivedChannels = async (firmId) => {
  const channelsRes = await from('firm_channels')
    .select('id, name, description, is_private, archived_at, archived_by, created_at')
    .eq('firm_id', firmId)
    .not('archived_at', 'is', null)
    .order('archived_at', { ascending: false });

  if (channelsRes.error) {
    const msg = channelsRes.error?.message || '';
    if (/column .*archived_at.*does not exist/i.test(msg)
        || /column .*is_private.*does not exist/i.test(msg)
        || /column .*archived_by.*does not exist/i.test(msg)) {
      // eslint-disable-next-line no-console
      console.warn('[listArchivedChannels] required column missing — apply migration 102 (archived_at/archived_by) and 051 (is_private). Returning empty for now.');
      return { data: [], error: null };
    }
    return channelsRes;
  }

  const channels = channelsRes.data || [];
  if (channels.length === 0) return { data: [], error: null };

  const archiverIds = Array.from(new Set(channels.map((c) => c.archived_by).filter(Boolean)));
  if (archiverIds.length === 0) {
    return { data: channels.map((c) => ({ ...c, archiver: null })), error: null };
  }

  const { data: archivers } = await from('users')
    .select('id, first_name, last_name, email')
    .in('id', archiverIds);
  const byId = Object.fromEntries((archivers || []).map((u) => [u.id, u]));

  return {
    data: channels.map((c) => ({ ...c, archiver: byId[c.archived_by] || null })),
    error: null,
  };
};

// Lightweight matter lookup for showing names in channel headers without
// dragging in the big matter detail payload.
export const fetchMatterSummary = (matterId) =>
  from('matters')
    .select('id, matter_number, title, status')
    .eq('id', matterId)
    .maybeSingle();

// Matter picker — caller's firm only, returns id + label fields for an
// autocomplete list. Capped at 50 to keep things snappy.
export const listMattersForPicker = (firmId, query = '') => {
  let q = from('matters')
    .select('id, matter_number, title, status')
    .eq('firm_id', firmId)
    .order('open_date', { ascending: false })
    .limit(50);
  if (query?.trim()) {
    const v = `%${query.trim()}%`;
    q = q.or(`title.ilike.${v},matter_number.ilike.${v}`);
  }
  return q;
};
export const listPinnedMessages       = (channelId)             => supabase.rpc('list_pinned_messages', { p_channel_id: channelId });
export const firmMessageThreadCounts  = (channelId)             => supabase.rpc('firm_message_thread_counts', { p_channel_id: channelId });
export const firmMessageThread        = (parentId)              => supabase.rpc('firm_message_thread', { p_parent: parentId });

export const listFirmMessageEdits = (messageId) =>
  from('firm_message_edits')
    .select('id, prior_body, edited_at, editor:users!edited_by(id, first_name, last_name, email)')
    .eq('message_id', messageId)
    .order('edited_at', { ascending: false });

// ─── Mention digest trigger (super-admin) ───────────────────────────────
export const sendMentionDigest = (firmId = null) =>
  supabase.functions.invoke('send-mention-digest', { body: firmId ? { firm_id: firmId } : {} });

// ---------------- remove unconfirmed invite -----------------
// Deletes a pending (unconfirmed) user from both public.users and auth.users.
// Must be called via an Edge Function because only the service-role key can
// delete from auth.users.  Falls back to just deleting the public row if the
// Edge Function isn't available (dev convenience).
export const removeUnconfirmedUser = async (userId) => {
  const { data: { session } } = await supabase.auth.getSession();
  try {
    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/remove-pending-user`,
      {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ user_id: userId }),
      },
    );
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return { error: new Error(body.error || 'Failed to remove user') };
    }
    return { error: null };
  } catch (err) {
    // Fallback: at least remove the public row so the UI cleans up
    await supabase.from('users').delete().eq('id', userId);
    return { error: err };
  }
};

// Permanently removes a confirmed team member from auth.users (and cascades
// to public.users).  Requires the delete-team-member Edge Function.
export const deleteTeamMember = async (userId) => {
  const { data: { session } } = await supabase.auth.getSession();
  try {
    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-team-member`,
      {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ user_id: userId }),
      },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { error: new Error(body.error || 'Failed to delete user') };
    return { error: null };
  } catch (err) {
    return { error: err };
  }
};

// Revokes a contact's portal access and deletes their auth.users entry.
// Safe to call even if the contact never had portal access (no-op).
export const deletePortalUser = async (contactId) => {
  const { data: { session } } = await supabase.auth.getSession();
  try {
    const res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-portal-user`,
      {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ contact_id: contactId }),
      },
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { error: new Error(body.error || 'Failed to revoke portal access') };
    return { error: null };
  } catch (err) {
    return { error: err };
  }
};

// ---------------- firm logo -----------------
// Uploads a logo image to the "logos" storage bucket and saves the public URL
// back to the firms row.  The bucket should be public (no signed URLs needed).
export const uploadFirmLogo = async (firmId, file) => {
  const ext  = file.name.split('.').pop().toLowerCase();
  const path = `${firmId}/logo.${ext}`;

  // upsert:true so re-uploading overwrites the old file
  const { error: uploadErr } = await supabase.storage
    .from('logos')
    .upload(path, file, { cacheControl: '3600', upsert: true, contentType: file.type });

  if (uploadErr) return { error: uploadErr };

  const { data: { publicUrl } } = supabase.storage.from('logos').getPublicUrl(path);

  // Bust the browser cache by appending a timestamp query param
  const logoUrl = `${publicUrl}?t=${Date.now()}`;

  const { error: updateErr } = await supabase
    .from('firms')
    .update({ logo_url: logoUrl })
    .eq('id', firmId);

  return { error: updateErr, url: logoUrl };
};

// ---------------- team member invite -----------------
// Sends an invitation email via the invite-team-member Edge Function.
// The Edge Function:
//   1. Pre-creates an employees row (status = 'invited') so the person
//      appears in the firm directory immediately.
//   2. Calls auth.admin.inviteUserByEmail() with rich metadata.
//   3. On first login, handle_new_auth_user links the auth user to the
//      employees row and flips status to 'active'.
export const inviteTeamMember = async (firmId, {
  email,
  firstName,
  middleName     = '',
  lastName,
  role           = 'Associate',
  title          = '',
  hourlyRate     = 0,
  practiceArea   = '',
  employmentType = 'full_time',
}) => {
  const { data: { session } } = await supabase.auth.getSession();
  let res;
  try {
    res = await fetch(
      `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/invite-team-member`,
      {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
          'origin':        window.location.origin,
        },
        body: JSON.stringify({
          firm_id:         firmId,
          email,
          first_name:      firstName,
          middle_name:     middleName,
          last_name:       lastName,
          role,
          title,
          hourly_rate:     hourlyRate,
          practice_area:   practiceArea,
          employment_type: employmentType,
        }),
      },
    );
  } catch (err) {
    // Network-level failure — DNS down, CORS blocked, offline, etc.
    return { data: null, error: { message: `Could not reach the invite service: ${err?.message || 'network error'}. Check your connection and try again.` } };
  }
  // 404 specifically means the Edge Function isn't deployed in this
  // Supabase project. Surface a clear admin-level message instead of
  // the generic "Invite failed" — saves a support round-trip when a
  // firm rolls out the app without running `supabase functions deploy`.
  if (res.status === 404) {
    return {
      data: null,
      error: { message: 'The invite Edge Function is not deployed. An admin needs to run `supabase functions deploy invite-team-member` in the project.' },
    };
  }
  let json = null;
  try { json = await res.json(); } catch { /* non-JSON response */ }
  if (!res.ok) {
    const reason = json?.error || json?.message || `HTTP ${res.status}`;
    // Migration 141: the seat-enforcement trigger raises errcode
    // '54000' (program_limit_exceeded). The Edge Function surfaces
    // the message verbatim; we detect either the code or the
    // "Seat limit reached" prefix to give the user a clean message
    // (vs. the generic "Invite failed:" wrapper).
    if (/54000|seat limit reached/i.test(reason)) {
      return {
        data: null,
        error: {
          code: 'SEAT_LIMIT',
          message: reason.replace(/^.*?seat limit reached/i, 'Seat limit reached'),
        },
      };
    }
    return { data: null, error: { message: `Invite failed: ${reason}` } };
  }
  return { data: json, error: null };
};

// Re-send an invite email to a pending team member.
// Uses the same Edge Function — Supabase will re-send the setup link.
export const resendInvite = (firmId, employee) =>
  inviteTeamMember(firmId, {
    email:          employee.email,
    firstName:      employee.first_name,
    middleName:     employee.middle_name  || '',
    lastName:       employee.last_name   || '',
    role:           employee.role        || 'Associate',
    title:          employee.title       || '',
    hourlyRate:     employee.hourly_rate || 0,
    practiceArea:   employee.practice_area   || '',
    employmentType: employee.employment_type || 'full_time',
  });

// Add a staff record without granting login access.
// For non-attorney staff, contractors, or anyone tracked in HR
// who doesn't need to log in to the system.
//
// Preferred path: SECURITY DEFINER RPC (migration 120) which resolves
// firm_id server-side from auth.uid() — bypasses the RLS WITH CHECK
// failure mode that hit clients with stale session state. The RPC is
// also tolerant of missing HR columns (migration 081) and degrades to a
// minimal insert.
//
// Fallback path (older DB without migration 120): the direct insert with
// retry-on-schema-cache-miss that was here before.
export const addStaffRecord = async (firmId, {
  firstName,
  middleName     = '',
  lastName,
  email          = '',
  role           = 'Associate',
  title          = '',
  hourlyRate     = 0,
  practiceArea   = '',
  employmentType = 'full_time',
  notes          = '',
}) => {
  // 1. Try the RPC first.
  const rpcRes = await supabase.rpc('add_staff_record', {
    p_first_name:      firstName,
    p_last_name:       lastName,
    p_email:           email          || null,
    p_role:            role,
    p_title:           title          || null,
    p_middle_name:     middleName     || null,
    p_hourly_rate:     hourlyRate     || 0,
    p_practice_area:   practiceArea   || null,
    p_employment_type: employmentType || 'full_time',
    p_notes:           notes          || null,
  });
  if (!rpcRes.error) {
    // RPC returns a single row (the employees record).
    const row = Array.isArray(rpcRes.data) ? rpcRes.data[0] : rpcRes.data;
    return { data: row, error: null };
  }

  // RPC missing on this DB — fall through to legacy direct-insert path.
  const looksMissing = /PGRST202|Could not find the function|function .*add_staff_record/i.test(
    rpcRes.error.message || '',
  );
  if (!looksMissing) return { data: null, error: rpcRes.error };

  // ── Legacy direct insert ─────────────────────────────────────────────
  const full = {
    firm_id:         firmId,
    first_name:      firstName,
    middle_name:     middleName     || null,
    last_name:       lastName,
    email:           email          || null,
    role,
    title:           title          || null,
    hourly_rate:     hourlyRate     || 0,
    practice_area:   practiceArea   || null,
    employment_type: employmentType,
    notes:           notes          || null,
    status:          'active',
  };

  const result = await from('employees').insert(full).select().single();
  if (!result.error) return result;

  // Schema-cache retry: strip HR columns from migration 081 and retry.
  const msg = result.error.message || '';
  const missingMatch = /'(middle_name|practice_area|employment_type)' column/i.exec(msg);
  if (!missingMatch) return result;

  const minimal = { ...full };
  delete minimal.middle_name;
  delete minimal.practice_area;
  delete minimal.employment_type;

  // eslint-disable-next-line no-console
  console.warn(
    '[addStaffRecord] retrying without HR columns — apply migration 081 or run `notify pgrst, \'reload schema\';` in the Supabase SQL Editor to pick them up.',
  );

  return from('employees').insert(minimal).select().single();
};

// ---------------- public intake (anon, no firm context) -----------------
// Used by the public intake form page — does NOT require authentication.
// RLS policy "public_read_active_intake_form" allows anon SELECT on active forms.
// RLS policy "public_submit_intake" allows anon INSERT on intake_submissions.

export const getPublicIntakeForm = (formId) =>
  supabase
    .from('intake_forms')
    .select('id, name, description, schema, firm_id, firm:firms(id, name, logo_url)')
    .eq('id', formId)
    .eq('is_active', true)
    .maybeSingle()
    .then(({ data, error }) => ({
      data: data ? { ...data, fields: data.schema } : null,
      error,
    }));

export const submitPublicIntake = (formId, firmId, answers) =>
  supabase
    .from('intake_submissions')
    .insert({
      intake_form_id: formId,
      firm_id:        firmId,
      data:           answers,       // schema column is "data" (jsonb)
      lead_status:    'new',         // schema column is "lead_status"
      submitted_at:   new Date().toISOString(),
    })
    .select()
    .single();

// ---------------- global search -----------------
// Searches matters, contacts, and documents in parallel for a given query.
// Returns { matters, contacts, documents } — each an array (empty on error).
//
// Note: uses separate .ilike() calls per column rather than .or() with % wildcards,
// because embedding % inside the .or() filter string can cause URL percent-encoding
// issues in PostgREST. Results are deduplicated by id client-side.
export const globalSearch = async (firmId, query) => {
  if (!firmId || !query) return { matters: [], contacts: [], documents: [] };

  const q = `%${query}%`;
  const MATTER_SELECT  = 'id, matter_number, title, status, practice_area, client:contacts!matters_client_id_fkey(first_name, last_name, organization)';
  const CONTACT_SELECT = 'id, first_name, last_name, email, organization, contact_type';

  const [
    mByTitle, mByNumber,
    cByLast, cByFirst, cByEmail, cByOrg,
    docRes,
  ] = await Promise.all([
    from('matters').select(MATTER_SELECT).eq('firm_id', firmId).ilike('title', q).limit(5),
    from('matters').select(MATTER_SELECT).eq('firm_id', firmId).ilike('matter_number', q).limit(5),
    from('contacts').select(CONTACT_SELECT).eq('firm_id', firmId).ilike('last_name', q).limit(5),
    from('contacts').select(CONTACT_SELECT).eq('firm_id', firmId).ilike('first_name', q).limit(5),
    from('contacts').select(CONTACT_SELECT).eq('firm_id', firmId).ilike('email', q).limit(3),
    from('contacts').select(CONTACT_SELECT).eq('firm_id', firmId).ilike('organization', q).limit(3),
    from('documents').select('id, name, category, matter_id').eq('firm_id', firmId).ilike('name', q).limit(5),
  ]);

  // Merge and deduplicate by id within each entity type
  const dedup = (arrays) => {
    const seen = new Set();
    return arrays.flat().filter((r) => r && !seen.has(r.id) && seen.add(r.id));
  };

  return {
    matters:   dedup([mByTitle.data, mByNumber.data]).slice(0, 5),
    contacts:  dedup([cByLast.data, cByFirst.data, cByEmail.data, cByOrg.data]).slice(0, 5),
    documents: (docRes.data || []).slice(0, 5),
  };
};

// ─── Portal document workflow (migration 112) ────────────────────────
// Firm side
export const createPortalDocumentRequest = (matterId, contactId, title, description = null, dueAt = null) =>
  supabase.rpc('create_portal_document_request', {
    p_matter_id: matterId,
    p_contact_id: contactId,
    p_title: title,
    p_description: description,
    p_due_at: dueAt,
  });

export const cancelPortalDocumentRequest = (id) =>
  supabase.rpc('cancel_portal_document_request', { p_request_id: id });

export const acceptPortalDocumentRequest = (id, opts = {}) =>
  supabase.rpc('accept_portal_document_request', {
    p_request_id: id,
    p_title: opts.title || null,
    p_category: opts.category || 'Client upload',
    p_classification: opts.classification || 'internal',
    p_make_visible: !!opts.makeVisible,
  });

export const rejectPortalDocumentRequest = (id, note = null) =>
  supabase.rpc('reject_portal_document_request', { p_request_id: id, p_note: note });

export const listPortalDocumentRequestsForFirm = (matterId = null) =>
  supabase.rpc('list_portal_document_requests_for_firm', { p_matter_id: matterId });

// Portal side
export const respondPortalDocumentRequest = (id, path, filename, mime, size) =>
  supabase.rpc('respond_portal_document_request', {
    p_request_id: id, p_path: path, p_filename: filename, p_mime: mime, p_size: size,
  });

export const portalUploadDocument = (matterId, path, title, filename, mime, size) =>
  supabase.rpc('portal_upload_document', {
    p_matter_id: matterId, p_path: path, p_title: title,
    p_filename: filename, p_mime: mime, p_size: size,
  });

export const portalDocumentAccess = (documentId) =>
  supabase.rpc('portal_document_access', { p_document_id: documentId });

export const listPortalDocumentRequestsForClient = () =>
  supabase.rpc('list_portal_document_requests_for_client');

// Working portal download: check access via RPC, then create a short-lived
// signed URL through Storage. Returns { signedUrl, title, mime, error }.
export const portalDocumentSignedUrl = async (documentId) => {
  const { data, error } = await portalDocumentAccess(documentId);
  if (error) return { error };
  if (!data?.allowed || !data?.file_path) {
    return { error: { message: 'Not allowed' } };
  }
  const { data: signed, error: signErr } = await supabase
    .storage.from('documents').createSignedUrl(data.file_path, 3600);
  if (signErr) return { error: signErr };
  return {
    signedUrl: signed?.signedUrl || null,
    title:     data.title,
    mime:      data.mime_type,
    error:     null,
  };
};

// Upload helper for portal client. Generates a deterministic storage path
// under <firm_id>/portal-uploads/<contact_id>/<matter_id_or_'none'>/<uuid>-<filename>.
// The firm_id-first prefix matches the existing storage policy from
// migration 006 so firm members can read/manage uploads automatically.
// Returns { path, error }. Caller then calls respondPortalDocumentRequest
// or portalUploadDocument to register the upload.
export const uploadPortalFile = async (firmId, contactId, matterId, file) => {
  if (!firmId || !contactId || !file) return { error: { message: 'missing args' } };
  const safeName = (file.name || 'upload').replace(/[^\w.-]+/g, '_');
  const uuid = (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const matterSeg = matterId || 'none';
  const path = `${firmId}/portal-uploads/${contactId}/${matterSeg}/${uuid}-${safeName}`;
  const { error } = await supabase.storage
    .from('documents')
    .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false });
  if (error) return { error };
  return { path, filename: safeName, mime: file.type || 'application/octet-stream', size: file.size };
};

// ---------------- document templates (firm-managed boilerplate) ---------------
//
// CRUD on public.document_templates. The table + firm_tenancy RLS policy
// were installed in migrations 001 + 078 — every operation here is
// firm-scoped automatically by `firm_id = current_firm_id()` (Postgres
// enforces tenant isolation; the client just sends ordinary queries).
//
// Templates store plain-text bodies with {{path.to.value}} placeholders.
// The frontend uses `renderTemplate()` from src/lib/templateRender.js to
// substitute values at render time. We don't store rendered output here;
// each render is computed live from current firm + matter + client + user
// state, so the template always uses the latest data.

export const listDocumentTemplates = (firmId) =>
  from('document_templates')
    .select('id, name, description, category, body, merge_fields, is_active, created_by, created_at, updated_at')
    .eq('firm_id', firmId)
    .order('name', { ascending: true });

export const getDocumentTemplate = (id) =>
  from('document_templates')
    .select('*')
    .eq('id', id)
    .maybeSingle();

export const createDocumentTemplate = ({ firmId, name, description, category, body, mergeFields }) =>
  from('document_templates')
    .insert({
      firm_id:     firmId,
      name:        (name || '').trim(),
      description: (description || '').trim() || null,
      category:    (category || '').trim() || null,
      body:        body || '',
      merge_fields: mergeFields || [],
      is_active:   true,
    })
    .select()
    .single();

export const updateDocumentTemplate = (id, patch) => {
  // Normalize incoming patch — only forward known columns so a typo in
  // a JSX form field can't accidentally write to a stranger column.
  const allowed = {};
  if (patch.name        !== undefined) allowed.name         = String(patch.name).trim();
  if (patch.description !== undefined) allowed.description  = patch.description ? String(patch.description).trim() : null;
  if (patch.category    !== undefined) allowed.category     = patch.category    ? String(patch.category).trim()    : null;
  if (patch.body        !== undefined) allowed.body         = patch.body || '';
  if (patch.mergeFields !== undefined) allowed.merge_fields = patch.mergeFields || [];
  if (patch.isActive    !== undefined) allowed.is_active    = !!patch.isActive;
  allowed.updated_at = new Date().toISOString();
  return from('document_templates').update(allowed).eq('id', id).select().single();
};

export const deleteDocumentTemplate = (id) =>
  from('document_templates').delete().eq('id', id);
